<template>
  <div class="extended-calendar">
    <h4 class="table-page-header">
      Calendar
      <el-tooltip v-if="canCreateShifts" content="Add New Shift">
        <el-button type="text" @click="createShift">
          <i class="fa-solid fa-circle-plus" style="font-size: 22px"></i>
        </el-button>
      </el-tooltip>
    </h4>

    <div class="compact-filter-section">
      <!-- Navigation Controls Row -->
      <div class="filter-row-compact navigation-row">
        <div class="filter-group-compact view-group">
          <AIVSelect
            v-model="currentViewSelect"
            :options="viewOptions"
            placeholder="Select View"
            @input="handleViewChange"
            class="compact-select"
          />
        </div>
        <!-- When NOT in custom mode: show prev/next + date picker -->
        <template v-if="currentViewSelect !== 'customRange'">
          <div class="filter-group-compact date-group date-nav-group">
            <button @click="goToPrevious" class="compact-nav-btn">
              <i class="fas fa-chevron-left"></i>
            </button>
            <el-date-picker
              ref="datePicker"
              v-model="selectedDate"
              :type="datePickerType"
              :format="dateFormat"
              :placeholder="placeholderText"
              @change="handleDateChange"
              class="compact-date-picker"
              :value-format="'yyyy-MM-dd'"
            >
              <template #prefix>
                <i class="el-input__icon el-icon-date"></i>
              </template>
            </el-date-picker>
            <button @click="goToNext" class="compact-nav-btn">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </template>
        <!-- When IN custom mode: show date range picker -->
        <template v-else>
          <div class="filter-group-compact date-group custom-date-range-group">
            <el-date-picker
              v-model="customDateRange"
              type="daterange"
              range-separator="-"
              start-placeholder="Start date"
              end-placeholder="End date"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              @change="handleCustomDateRangeChange"
              class="compact-date-picker"
            />
          </div>
        </template>
        <div class="filter-group-compact today-group">
          <button @click="handleTodayClick" class="compact-today-btn">
            <i class="fas fa-bullseye"></i>
            Today
          </button>
        </div>
        <div
          v-if="userRole === 'Caregiver' && totalVisibleMiles !== null"
          class="filter-group-compact mileage-total-chip"
        >
          <i class="fas fa-car"></i>
          {{ totalVisibleMiles.toFixed(1) }} mi
        </div>
      </div>

      <!-- Data Filters Row -->
      <div class="filter-row-compact data-row">
        <div class="filter-group-compact">
          <AIVSelect
            v-model="selectedShiftStatus"
            :options="shiftStatuses"
            placeholder="Status"
            class="compact-select"
          />
        </div>
        <div v-if="userRole !== 'Caregiver'" class="filter-group-compact">
          <AIVSelect
            v-model="selectedCaregiver"
            :options="caregivers"
            placeholder="Caregiver"
            :multiSelect="true"
            :searchable="true"
            :clearable="true"
            class="compact-select"
          />
        </div>
        <div class="filter-group-compact">
          <AIVSelect
            v-model="selectedClient"
            :options="clients"
            placeholder="Client"
            class="compact-select"
          />
        </div>
        <div v-if="userRole !== 'Caregiver'" class="filter-group-compact">
          <AIVSelect
            v-model="selectedAgency"
            :options="agencies"
            placeholder="Agency"
            class="compact-select"
          />
        </div>
        <div v-if="userRole === 'Admin'" class="filter-group-compact">
          <AIVSelect
            v-model="selectedAdmin"
            :options="admins"
            placeholder="Admin"
            :multiSelect="true"
            :searchable="true"
            :clearable="true"
            class="compact-select"
          />
        </div>
        <div class="filter-group-compact">
          <AIVSelect
            v-model="selectedShiftType"
            :options="shiftTypes"
            placeholder="Type"
            class="compact-select"
          />
        </div>
        <div v-if="userRole !== 'Caregiver'" class="filter-group-compact">
          <AIVSelect
            v-model="selectedPayer"
            :options="payers"
            placeholder="Payer"
            class="compact-select"
          />
        </div>
      </div>

      <!-- Legend Row -->
      <div class="filter-row-compact legend-row">
        <div class="legend-groups">
          <!-- Shift Status Group -->
          <div class="legend-group">
            <div class="legend-group-header">
              <i class="fas fa-info-circle"></i>
              <span>Status:</span>
            </div>
            <div
              class="legend-item"
              v-if="userRole === 'Admin' || userRole === 'Lead'"
            >
              <div class="legend-color status-unassigned"></div>
              <span>Unassigned</span>
            </div>
            <div class="legend-item">
              <div class="legend-color status-scheduled"></div>
              <span>Scheduled</span>
            </div>
            <div class="legend-item">
              <div class="legend-color status-clocked-in"></div>
              <span>Clocked In</span>
            </div>
            <div class="legend-item">
              <div class="legend-color status-clocked-out"></div>
              <span>Clocked Out</span>
            </div>
            <div class="legend-item">
              <div class="legend-color status-approved"></div>
              <span>Approved</span>
            </div>
            <div class="legend-item">
              <div class="legend-color status-missed"></div>
              <span>Missed</span>
            </div>
            <div
              class="legend-item"
              v-if="userRole === 'Admin' || userRole === 'Lead'"
            >
              <div class="legend-color status-error"></div>
              <span>Error</span>
            </div>
            <div
              class="legend-item"
              v-if="userRole === 'Admin' || userRole === 'Lead'"
            >
              <div class="legend-color status-canceled"></div>
              <span>Canceled</span>
            </div>
            <div
              class="legend-item"
              v-if="userRole === 'Admin' || userRole === 'Lead'"
            >
              <div class="legend-color status-available"></div>
              <span>Available</span>
            </div>
          </div>

          <div class="legend-divider"></div>

          <!-- Shift Type Group -->
          <div class="legend-group">
            <div class="legend-group-header">
              <i class="fas fa-clock"></i>
              <span>Type:</span>
            </div>
            <div class="legend-item">
              <div class="legend-color shift-type-1-on-1"></div>
              <span>1-on-1</span>
            </div>
            <div class="legend-item">
              <div class="legend-color shift-type-group"></div>
              <span>Group</span>
            </div>
          </div>

          <template v-if="userRole !== 'Caregiver'">
            <div class="legend-divider"></div>

            <!-- Payer Group -->
            <div class="legend-group">
              <div class="legend-group-header">
                <i class="fas fa-credit-card"></i>
                <span>Payer:</span>
              </div>
              <div class="legend-item">
                <div class="legend-color payer-dspd"></div>
                <span>DSPD</span>
              </div>
              <div class="legend-item">
                <div class="legend-color payer-vr"></div>
                <span>VR</span>
              </div>
              <div class="legend-item">
                <div class="legend-color payer-private"></div>
                <span>Private</span>
              </div>
              <div class="legend-item">
                <div class="legend-color payer-non-service"></div>
                <span>Non Service</span>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div class="ec-views">
      <MonthView
        v-if="currentView === 'dayGridMonth' || (currentView === 'customRange' && customRangeDays.length > 7)"
        :currentMonth="effectiveCurrentMonth"
        :daysInMonth="effectiveDaysInMonth"
        :dayNames="dayNames"
        :eventsForDate="shiftsForDate"
        :isToday="isToday"
        :userRole="userRole"
        :isInCustomRange="isInCustomRange"
        @show-details="viewShiftDetails"
      />
      <DayView
        v-if="currentView === 'timeGridDay'"
        :dayViewDate="dayViewDate[0]"
        :dayNames="dayNames"
        :timeSlots="timeSlots"
        :eventsForDate="shiftsForDate"
        :isToday="isToday"
        :lineStyle="dayViewLineStyle"
        :computeEventStyle="computeShiftStyle"
        :userRole="userRole"
        :approvedTimeOff="selectedCaregiverApprovedTimeOff"
        @show-details="viewShiftDetails"
      />
      <WeekView
        v-if="currentView === 'timeGridWeek' || (currentView === 'customRange' && customRangeDays.length > 0 && customRangeDays.length <= 7)"
        :weekDates="effectiveWeekDates"
        :dayNames="dayNames"
        :timeSlots="timeSlots"
        :eventsForDate="shiftsForDate"
        :isTodayInWeek="effectiveIsTodayInRange"
        :lineStyle="weekViewLineStyle"
        :computeEventStyle="computeShiftStyle"
        :userRole="userRole"
        :approvedTimeOff="selectedCaregiverApprovedTimeOff"
        @show-details="viewShiftDetails"
      />
      <TeamView
        v-if="currentView === 'teamView'"
        :caregivers="teamViewCaregivers"
        :timeSlots="timeSlots"
        :shifts="teamViewShifts"
        :currentDate="dayViewDate[0]"
        :isToday="isTeamViewToday"
        :lineStyle="dayViewLineStyle"
        :userRole="userRole"
        :caregiverTimeOffs="teamViewCaregiverTimeOffs"
        @show-details="viewShiftDetails"
      />
    </div>

    <ShiftModal
      :visible.sync="showShiftModal"
      :shift-being-edited="shiftBeingEdited"
      :available-clients="availableClients"
      :available-caregivers="availableCaregivers"
      :allow-delete="true"
      @cancel="closeShiftModal"
      @save="saveShift"
      @delete="handleEditModalDelete"
    />

    <ShiftActionModal
      :visible.sync="showShiftActionModal"
      :shift="selectedShiftForActions"
      :viewOnly="isViewOnlyMode"
      @clock-in="handleShiftClockIn"
      @clock-out="handleShiftClockOut"
      @admin-clock-out="handleAdminClockOut"
      @start-break="handleShiftStartBreak"
      @edit-shift="handleShiftEdit"
      @edit-shift-notes="handleEditShiftNotes"
      @edit-clock-in-time="handleEditClockInTime"
      @edit-clock-out-time="handleEditClockOutTime"
      @approve-shift="handleApproveShift"
      @undo-approval="handleUndoApproval"
      @approve-missed-shift="handleApproveMissedShift"
      @cancel-shift="handleCancelShift"
      @delete-shift="handleDeleteShift"
      @open-tasks="handleOpenGroupTasks"
    />

    <!-- Delete Recurring Shift Dialog -->
    <DeleteRecurringShiftDialog
      :visible.sync="deleteRecurringDialog.visible"
      @this-only="deleteThisShiftOnly"
      @this-and-future="deleteThisAndFutureShifts"
      @all-shifts="deleteAllShifts"
    />

    <GroupShiftTasksModal
      :visible.sync="showGroupTasksModal"
      :shift="groupTasksShift"
      :user-id="userId"
      @refresh="fetchShifts"
    />

    <ClockInModal
      :visible.sync="showClockInModal"
      :shift="currentShift"
      :user-id="userId"
      @clock-in-success="handleClockInSuccess"
    />

    <ClockOutModal
      :visible.sync="showClockOutModal"
      :shift="currentShift"
      :user-id="userId"
      @clock-out-success="handleClockOutSuccess"
    />

    <AdminClockOutModal
      :visible.sync="showAdminClockOutModal"
      :shift="shiftToAdminClockOut"
      @clocked-out="fetchShifts"
    />

    <StartBreakModal
      :visible.sync="showStartBreakModal"
      :shift="currentShift"
      :user-id="userId"
      @break-action-success="onBreakActionSuccess"
    />

    <EditClockInTimeModal
      :visible.sync="showEditClockInTimeModal"
      :shift="shiftToEditClockInTime"
      @clock-in-updated="fetchShifts"
    />

    <EditClockOutTimeModal
      :visible.sync="showEditClockOutTimeModal"
      :shift="shiftToEditClockOutTime"
      @clock-times-updated="fetchShifts"
    />

    <EditShiftNotesModal
      :visible.sync="showEditShiftNotesModal"
      :shift="shiftToEditNotes"
      @updated="fetchShifts"
    />

    <ApproveShiftModal
      :visible.sync="showApproveShiftModal"
      :shift="shiftToApprove"
      @shift-approved="fetchShifts"
    />
    <ApproveMissedShift
      :visible.sync="showApproveMissedShiftModal"
      :shift="shiftToApproveMissed"
      @shift-approved="fetchShifts"
    />
  </div>
</template>

<script>
import MonthView from "@/pages/Calendar/MonthView.vue";
import DayView from "@/pages/Calendar/DayView.vue";
import WeekView from "@/pages/Calendar/WeekView.vue";
import TeamView from "@/pages/Calendar/TeamView.vue";
import AIVSelect from "@/components/AIVSelect.vue";
import ShiftModal from "@/components/ShiftModal.vue";
import ShiftActionModal from "@/components/ShiftActionModal.vue";
import ClockInModal from "@/components/ClockInModal.vue";
import ClockOutModal from "@/components/ClockOutModal.vue";
import AdminClockOutModal from "@/components/AdminClockOutModal.vue";
import StartBreakModal from "@/components/StartBreakModal.vue";
import EditClockInTimeModal from "@/components/EditClockInTimeModal.vue";
import EditClockOutTimeModal from "@/components/EditClockOutTimeModal.vue";
import EditShiftNotesModal from "@/components/EditShiftNotesModal.vue";
import ApproveShiftModal from "@/components/ApproveShiftModal.vue";
import ApproveMissedShift from "@/components/ApproveMissedShift.vue";
import GroupShiftTasksModal from "@/components/GroupShiftTasksModal.vue";
import DeleteRecurringShiftDialog from "@/components/DeleteRecurringShiftDialog.vue";
import { shiftsService } from "@/serviceHandlers/shiftsService";
import { caregiversService } from "@/serviceHandlers/caregiversService";
import { clientsService } from "@/serviceHandlers/clientsService";
import { deletedPotentialShiftsService } from "@/serviceHandlers/deletedPotentialShiftsService";
import { officesService } from "@/serviceHandlers/officesService";
import { authorizationsService } from "@/serviceHandlers/authorizationsService";
import { schedulesService } from "@/serviceHandlers/schedulesService";
import { useLoginStore } from "@/stores/loginStore";
import { EventBus, EVENTS } from "@/utils/eventBus";
import { isDSPDGroupShift, getCaregiverShiftStatus, getStatusIcon, caregiverNameWithRole, primaryCaregiverOf } from "@/utils/shiftStatus";
import { milesForCaregiver, totalShiftMiles } from "@/utils/shiftMiles";
import conflictMessageMixin from "@/mixins/conflictMessageMixin";

export default {
  name: "ExtendedCalendar",
  mixins: [conflictMessageMixin],
  components: {
    WeekView,
    DayView,
    MonthView,
    TeamView,
    AIVSelect,
    ShiftModal,
    ShiftActionModal,
    AdminClockOutModal,
    ClockInModal,
    ClockOutModal,
    StartBreakModal,
    EditClockInTimeModal,
    EditClockOutTimeModal,
    EditShiftNotesModal,
    ApproveShiftModal,
    ApproveMissedShift,
    GroupShiftTasksModal,
    DeleteRecurringShiftDialog,
  },

  data() {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const daysInMonth = [];
    {
      const firstOfMonth = new Date(year, month, 1);
      const firstWeekday = firstOfMonth.getDay();
      for (let i = 0; i < firstWeekday; i++) {
        daysInMonth.push(new Date(year, month, i - firstWeekday + 1));
      }
      let cursor = new Date(year, month, 1);
      while (cursor.getMonth() === month) {
        daysInMonth.push(new Date(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }
      const totalCells = daysInMonth.length;
      const trailing = (7 - (totalCells % 7)) % 7;
      for (let i = 1; i <= trailing; i++) {
        daysInMonth.push(new Date(year, month + 1, i));
      }
    }
    const timeSlots = [];
    for (let h = 6; h <= 22; h++) {
      const ampm = h >= 12 ? "PM" : "AM";
      const disp = h % 12 === 0 ? 12 : h % 12;
      timeSlots.push(`${disp} ${ampm}`);
    }
    const dayViewDate = [new Date(today)];
    return {
      currentView: "timeGridDay",
      currentMonth: new Date(year, month, 1),
      dayViewDate,
      dayNames: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      monthOptions: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
      todayClicked: false,
      daysInMonth,
      timeSlots,
      shifts: [],
      selectedShiftStatus: null,
      selectedCaregiver: [],
      selectedClient: null,
      selectedPayer: null,
      selectedShiftType: null,
      selectedAgency: null,
      selectedAdmin: [],
      teamViewCaregiverTimeOffs: {},
      selectedShift: null,
      currentViewSelect: "timeGridDay",
      selectedDate: new Date(),
      availableAgencies: [],
      availableAdmins: [],
      availableClients: [],
      availableCaregivers: [],
      selectedCaregiverApprovedTimeOff: [],
      payerOptions: [
        { id: "DSPD", name: "DSPD" },
        { id: "VR", name: "VR" },
        { id: "Non Service", name: "Non Service" },
      ],
      shiftTypeOptions: [
        { id: "1-on-1", name: "1-on-1" },
        { id: "Group", name: "Group" },
      ],
      showShiftModal: false,
      shiftBeingEdited: null,
      showShiftActionModal: false,
      selectedShiftForActions: null,
      isViewOnlyMode: false,
      deleteRecurringDialog: { visible: false, shift: null },
      showClockInModal: false,
      showClockOutModal: false,
      showAdminClockOutModal: false,
      shiftToAdminClockOut: null,
      showStartBreakModal: false,
      currentShift: null,
      selectedGroupClients: [],
      showEditClockInTimeModal: false,
      shiftToEditClockInTime: null,
      showEditClockOutTimeModal: false,
      shiftToEditClockOutTime: null,
      showEditShiftNotesModal: false,
      shiftToEditNotes: null,
      showApproveShiftModal: false,
      shiftToApprove: null,
      showApproveMissedShiftModal: false,
      shiftToApproveMissed: null,
      showGroupTasksModal: false,
      groupTasksShift: null,
      // Custom date range properties
      customDateRange: null, // [startDate, endDate] array for custom mode
      customRangeDays: [], // Array of Date objects in the range
    };
  },
  async created() {
    await Promise.all([
      this.fetchShifts(),
      this.loadAgencies(),
      this.loadAdmins(),
      this.loadCaregivers(true),
      this.loadClients(),
    ]);
  },
  async mounted() {
    this.$nextTick(() => {
      this.updateDatePickerDisplay();
    });

    // Listen for clock in/out events from navbar
    EventBus.$on(EVENTS.SHIFT_CLOCK_IN, this.handleClockInEvent);
    EventBus.$on(EVENTS.SHIFT_CLOCK_OUT, this.handleClockOutEvent);
  },
  beforeDestroy() {
    // Clean up event listeners
    EventBus.$off(EVENTS.SHIFT_CLOCK_IN, this.handleClockInEvent);
    EventBus.$off(EVENTS.SHIFT_CLOCK_OUT, this.handleClockOutEvent);
  },
  watch: {
    selectedShiftStatus() {
      this.fetchShifts();
    },
    selectedCaregiver: {
      handler() {
        // Clear approved time off when caregiver selection changes
        this.selectedCaregiverApprovedTimeOff = [];
        this.teamViewCaregiverTimeOffs = {};
        this.fetchShifts();
      },
      deep: true,
    },
    selectedClient() {
      this.fetchShifts();
    },
    selectedPayer() {
      this.fetchShifts();
    },
    selectedShiftType() {
      this.fetchShifts();
    },
    selectedAgency() {
      this.fetchShifts();
    },
    selectedAdmin: {
      handler() {
        this.fetchShifts();
      },
      deep: true,
    },
    currentMonth() {
      this.fetchShifts(); // Refresh shifts when month changes
    },
    dayViewDate() {
      this.fetchShifts(); // Refresh shifts when day/week changes
    },
    customDateRange(newVal) {
      if (newVal && newVal.length === 2) {
        this.handleCustomDateRangeChange(newVal);
      }
    },
  },
  computed: {
    userRole() {
      const loginStore = useLoginStore();
      return loginStore.user?.user_type || "Admin";
    },
    userId() {
      const loginStore = useLoginStore();
      return loginStore.user?.id;
    },
    // Caregiver toolbar chip: sum of the recorded miles on the shifts
    // currently in view (null until at least one shift has a closed trip).
    totalVisibleMiles() {
      let total = null;
      for (const shift of this.shifts) {
        if (shift.miles != null) total = (total || 0) + shift.miles;
      }
      return total;
    },
    canCreateShifts() {
      const role = this.userRole;
      return (
        role === "Admin" || role === "Lead" || role === "Employment Specialist"
      );
    },
    currentTimeRatio() {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinutes = now.getMinutes();

      // Only show line if current time is within display range (6AM-10PM)
      if (currentHour < 6 || currentHour > 22) {
        return -1; // Hide line when outside 6am-10pm range
      }

      // Calculate position based on slot index (6AM = 0, 7AM = 1, etc.)
      const currentSlotIndex = currentHour - 6; // 6AM = 0, 7AM = 1, etc.
      const currentPosition = currentSlotIndex + currentMinutes / 60;

      // We have 17 time slots total, so divide by 17
      return currentPosition / 17;
    },
    dayViewLineStyle() {
      const ratio = this.currentTimeRatio;
      if (ratio < 0) {
        return { display: "none" }; // Hide when outside time range
      }
      return {
        left: `${ratio * 100}%`,
      };
    },
    isTodayInWeek() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const focused = this.dayViewDate[0] || new Date();
      const weekStart = new Date(focused);
      weekStart.setHours(0, 0, 0, 0);
      weekStart.setDate(focused.getDate() - focused.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(0, 0, 0, 0);
      return today >= weekStart && today <= weekEnd;
    },
    weekViewLineStyle() {
      const ratio = this.currentTimeRatio;
      if (ratio < 0) {
        return { display: "none" }; // Hide when outside time range
      }
      return {
        left: `${ratio * 100}%`,
      };
    },
    shiftStatuses() {
      const statuses = [];

      // Only show Unassigned option for Admins and Leads
      if (this.userRole === "Admin" || this.userRole === "Lead") {
        statuses.push({ id: "unassigned", name: "Unassigned" });
      }

      statuses.push(
        { id: "scheduled", name: "Scheduled" },
        { id: "clocked-in", name: "Clocked In" },
        { id: "clocked-out", name: "Clocked Out" },
        { id: "approved", name: "Approved" },
        { id: "missed", name: "Missed" },
        { id: "canceled", name: "Canceled" },
      );

      return statuses;
    },
    caregivers() {
      // Use the properly loaded caregiver list with IDs instead of deriving from shifts
      return this.availableCaregivers || [];
    },
    clients() {
      // Use the properly loaded client list with IDs instead of deriving from shifts
      return this.availableClients || [];
    },
    payers() {
      return this.payerOptions;
    },
    shiftTypes() {
      return this.shiftTypeOptions;
    },
    agencies() {
      // This will be populated by the loadAgencies method
      return this.availableAgencies || [];
    },
    admins() {
      // This will be populated by the loadAdmins method
      return this.availableAdmins || [];
    },
    viewOptions() {
      const options = [
        { id: "timeGridDay", name: "Day" },
        { id: "timeGridWeek", name: "Week" },
        { id: "dayGridMonth", name: "Month" },
        { id: "customRange", name: "Custom" },
      ];
      // Only show Team view for Admin and Lead roles
      if (this.userRole === "Admin" || this.userRole === "Lead") {
        options.splice(3, 0, { id: "teamView", name: "Day - Team" });
      }
      return options;
    },
    datePickerType() {
      switch (this.currentViewSelect) {
        case "timeGridDay":
        case "teamView":
          return "date";
        case "timeGridWeek":
          return "week";
        case "dayGridMonth":
          return "month";
        default:
          return "date";
      }
    },
    dateFormat() {
      switch (this.currentViewSelect) {
        case "timeGridDay":
        case "teamView":
          return "MMMM d, yyyy";
        case "timeGridWeek":
          return "MMMM d, yyyy";
        case "dayGridMonth":
          return "MMMM yyyy";
        default:
          return "MMMM d, yyyy";
      }
    },
    placeholderText() {
      switch (this.currentViewSelect) {
        case "timeGridDay":
        case "teamView":
          return "Select date";
        case "timeGridWeek":
          return "Select week";
        case "dayGridMonth":
          return "Select month";
        default:
          return "Select date";
      }
    },
    dateRangeDisplay() {
      if (!this.selectedDate) return "";

      // Ensure we have a proper Date object
      let date;
      if (this.selectedDate instanceof Date) {
        date = new Date(this.selectedDate.getTime());
      } else if (typeof this.selectedDate === "string") {
        // Parse as local date to avoid timezone issues
        const [year, month, day] = this.selectedDate.split("-").map(Number);
        date = new Date(year, month - 1, day);
      } else {
        return "";
      }

      // Check if date is valid
      if (isNaN(date.getTime())) return "";

      const monthNamesFull = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const monthNamesShort = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

      try {
        switch (this.currentViewSelect) {
          case "timeGridDay":
            // Show full date with month name
            return `${monthNamesFull[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;

          case "timeGridWeek":
            // Show full week range: "Sun, Dec 7, 2025 - Sat, Dec 13, 2025"
            const dayOfWeek = date.getDay();
            const sunday = new Date(date.getTime());
            sunday.setDate(date.getDate() - dayOfWeek);
            const saturday = new Date(sunday.getTime());
            saturday.setDate(sunday.getDate() + 6);

            const sundayStr = `${dayNames[0]}, ${monthNamesShort[sunday.getMonth()]} ${sunday.getDate()}, ${sunday.getFullYear()}`;
            const saturdayStr = `${dayNames[6]}, ${monthNamesShort[saturday.getMonth()]} ${saturday.getDate()}, ${saturday.getFullYear()}`;

            return `${sundayStr} - ${saturdayStr}`;

          case "dayGridMonth":
            // Show full month name and year
            return `${monthNamesFull[date.getMonth()]} ${date.getFullYear()}`;

          default:
            return date.toLocaleDateString();
        }
      } catch (error) {
        this.$notifyError("Error displaying date range");
        return "Invalid Date";
      }
    },
    // For WeekView: use custom range dates or standard week
    effectiveWeekDates() {
      if (this.currentView === "customRange" && this.customRangeDays.length > 0 && this.customRangeDays.length <= 7) {
        // Return only the selected dates - WeekView handles any array length
        return [...this.customRangeDays];
      }
      return Array.from({ length: 7 }, (_, i) => this.dateOfWeek(i));
    },
    // For MonthView: use custom range grid or standard month
    effectiveDaysInMonth() {
      if (this.currentView === "customRange" && this.customRangeDays.length > 7) {
        return this.calculateCustomRangeGrid();
      }
      return this.daysInMonth;
    },
    // Current month reference for MonthView
    effectiveCurrentMonth() {
      if (this.currentView === "customRange" && this.customRangeDays.length > 0) {
        return new Date(
          this.customRangeDays[0].getFullYear(),
          this.customRangeDays[0].getMonth(),
          1,
        );
      }
      return this.currentMonth;
    },
    // Check if today is in the displayed range
    effectiveIsTodayInRange() {
      if (this.currentView === "customRange") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return this.customRangeDays.some(
          (d) =>
            d.getFullYear() === today.getFullYear() &&
            d.getMonth() === today.getMonth() &&
            d.getDate() === today.getDate(),
        );
      }
      return this.isTodayInWeek;
    },
    // Check if a date is within the custom range
    isInCustomRange() {
      return (date) => {
        if (this.currentView !== "customRange" || this.customRangeDays.length === 0) {
          return true; // Not in custom mode, all days are "in range"
        }
        const dateTime = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        const startTime = new Date(
          this.customRangeDays[0].getFullYear(),
          this.customRangeDays[0].getMonth(),
          this.customRangeDays[0].getDate(),
        ).getTime();
        const endTime = new Date(
          this.customRangeDays[this.customRangeDays.length - 1].getFullYear(),
          this.customRangeDays[this.customRangeDays.length - 1].getMonth(),
          this.customRangeDays[this.customRangeDays.length - 1].getDate(),
        ).getTime();
        return dateTime >= startTime && dateTime <= endTime;
      };
    },
    // Team View computed properties
    teamViewCaregivers() {
      // Caregivers actually assigned to a (non-availability) shift in view.
      // Built from the same shifts TeamView renders, so every shift is
      // guaranteed a row to land in. The backend's Admin filter scopes shifts
      // by the CLIENT's admin (clients.id_admin), which does NOT line up with a
      // caregiver's supervising admin (users.id_admin) — filtering the rows by
      // id_admin alone silently drops the caregivers those shifts belong to.
      const assignedById = new Map();
      this.teamViewShifts.forEach((shift) => {
        if (shift.isAvailability) return;
        const cgs =
          (shift.originalData && shift.originalData.caregivers) ||
          shift.caregivers ||
          [];
        cgs.forEach((c) => {
          const id = Number(c.id);
          if (!id) return;
          if (!assignedById.has(id)) {
            assignedById.set(id, {
              id,
              name:
                c.name ||
                `${c.first_name || ""} ${c.last_name || ""}`.trim() ||
                "Caregiver",
              id_admin: null,
            });
          }
        });
      });

      let list = this.caregivers;

      // Filter by selected admin(s) if any are chosen. Keep the admin's own
      // caregivers (by id_admin) AND anyone assigned to a shift in view — the
      // backend already scoped those shifts to the admin's clients, so their
      // caregivers belong on this team's board regardless of id_admin.
      if (this.selectedAdmin && this.selectedAdmin.length > 0) {
        list = list.filter(
          (c) =>
            this.selectedAdmin.includes(c.id_admin) ||
            assignedById.has(Number(c.id))
        );
      }

      // If specific caregivers are selected in the filter, narrow further
      if (this.selectedCaregiver && this.selectedCaregiver.length > 0) {
        list = list.filter((c) =>
          this.selectedCaregiver.includes(c.id)
        );
        return list;
      }

      // Safety net: make sure every caregiver with a shift in view has a row,
      // even if they were excluded from the loaded caregiver list (e.g.
      // archived/inactive). Otherwise their shift renders nowhere.
      const present = new Set(list.map((c) => Number(c.id)));
      const extras = [];
      assignedById.forEach((meta, id) => {
        if (!present.has(id)) extras.push(meta);
      });
      return extras.length ? [...list, ...extras] : list;
    },
    teamViewShifts() {
      // Get shifts for the current team view date
      const dateObj = this.dayViewDate[0];
      return this.shiftsForDate(dateObj);
    },
    isTeamViewToday() {
      const dateObj = this.dayViewDate[0];
      return this.isToday(dateObj);
    },
  },
  methods: {
    handleClockInEvent() {
      // Refresh calendar when clock in occurs from navbar
      this.fetchShifts();
    },
    handleClockOutEvent() {
      // Refresh calendar when clock out occurs from navbar
      this.fetchShifts();
    },
    updateDatePickerDisplay() {
      this.$nextTick(() => {
        try {
          const input = this.$refs.datePicker?.$el?.querySelector("input");
          if (
            input &&
            this.currentViewSelect === "timeGridWeek" &&
            this.dateRangeDisplay
          ) {
            input.value = this.dateRangeDisplay;
          }
        } catch (error) {
          this.$notifyError("Error updating date picker display");
        }
      });
    },
    async fetchShifts() {
      this.$spinner.show();
      try {
        const { startDate, endDate } = this.getCalendarDateRange();

        const loginStore = useLoginStore();
        const userRole = loginStore.user?.user_type || "Admin";
        const userId = loginStore.user?.id || "1";

        const params = new URLSearchParams();
        params.append("startDate", startDate);
        params.append("endDate", endDate);
        params.append("userType", userRole);
        params.append("userId", userId);
        params.append("perPage", "5000");

        if (this.selectedAgency) {
          params.append("agencyId", this.selectedAgency);
        }

        if (this.selectedAdmin && this.selectedAdmin.length > 0) {
          params.append("adminId", this.selectedAdmin.join(','));
        }

        if (this.selectedClient) {
          params.append("clientId", this.selectedClient);
        }

        if (this.selectedCaregiver && this.selectedCaregiver.length > 0) {
          params.append("caregiverId", this.selectedCaregiver.join(','));

          // If viewing a caregiver in Day, Week, or Team view without a status filter,
          // include unassigned shifts so they can be assigned
          if (
            !this.selectedShiftStatus &&
            (this.currentView === "timeGridDay" ||
              this.currentView === "timeGridWeek" ||
              this.currentView === "teamView")
          ) {
            params.append("includeUnassigned", "true");
          }
        }

        if (this.selectedShiftStatus) {
          params.append("status", this.selectedShiftStatus);
        }

        if (this.selectedPayer) {
          params.append("payer", this.selectedPayer);
        }

        if (this.selectedShiftType) {
          params.append("shiftType", this.selectedShiftType);
        }

        // Use getByRole endpoint same as shifts page
        const response = await shiftsService.getByRole(params);
        const shifts = response.shifts || [];

        // Check if there are more shifts than the limit
        if (response.pagination && response.pagination.total > 5000) {
          this.$notifyError(
            "Only the first 5000 shifts are being displayed. Please use filters to narrow down the results.",
          );
        }

        // Process shifts for calendar display
        this.shifts = shifts.map((shift) =>
          this.processShiftForCalendar(shift),
        );

        // If filtering by "Unassigned" status, also fetch caregiver availability
        if (this.selectedShiftStatus === "unassigned") {
          await this.fetchCaregiverAvailability(startDate, endDate);
        }

        // If filtering by specific caregivers in Day, Week, or Team view,
        // fetch their availability (unassigned shifts are now included in main query via includeUnassigned flag)
        if (
          this.selectedCaregiver &&
          this.selectedCaregiver.length > 0 &&
          (this.currentView === "timeGridDay" ||
            this.currentView === "timeGridWeek")
        ) {
          // Non-team views: sequential fetch (typically 1 caregiver)
          for (const caregiverId of this.selectedCaregiver) {
            await this.fetchCaregiverAvailability(
              startDate,
              endDate,
              caregiverId,
            );
          }
        }

        // Team view: fetch availability for displayed caregivers in parallel
        if (this.currentView === "teamView") {
          const caregiversToFetch = this.teamViewCaregivers;
          await this.fetchTeamAvailability(startDate, endDate, caregiversToFetch);
        }
      } catch (error) {
        this.$notifyError("Error loading shifts: " + error);
        this.shifts = [];
      }
      this.$spinner.hide();
    },
    async fetchCaregiverAvailability(startDate, endDate, caregiverId = null) {
      try {
        if (!caregiverId) return;

        // Format dates as YYYY-MM-DD strings to avoid timezone conversion issues
        const formatDate = (date) => {
          // If already a string in YYYY-MM-DD format, return as-is
          if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}/.test(date)) {
            return date.split("T")[0]; // Strip time portion if present
          }

          // If it's a Date object, format it
          if (date && typeof date.getFullYear === "function") {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
          }

          // Fallback: try to create a Date object
          const dateObj = new Date(date);
          if (!isNaN(dateObj.getTime())) {
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, "0");
            const day = String(dateObj.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
          }

          return null;
        };

        const formattedStart = formatDate(startDate);
        const formattedEnd = formatDate(endDate);

        if (!formattedStart || !formattedEnd) {
          return;
        }

        // Load all schedules (no date range filter) so future/upcoming schedules
        // are included for proper date-based schedule resolution
        const response = await schedulesService.getByCaregiver(caregiverId);
        // Sort by start_date descending so the most recently started schedule
        // takes priority when multiple overlap on the same date
        const schedules = (response.schedules || []).sort((a, b) => {
          if (a.start_date > b.start_date) return -1;
          if (a.start_date < b.start_date) return 1;
          return 0;
        });
        const approvedTimeOff = response.approvedTimeOff || [];

        // Store approved time off for display
        this.selectedCaregiverApprovedTimeOff = approvedTimeOff;

        if (schedules.length === 0) {
          return;
        }

        // Get caregiver name for display
        let caregiverName = "Caregiver";
        try {
          const caregiver = await caregiversService.get(caregiverId);
          caregiverName = `${caregiver.first_name} ${caregiver.last_name}`;
        } catch (err) {
          // error
        }

        const dayNames = [
          "sunday",
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
          "saturday",
        ];

        const availabilityShifts = [];
        const start = new Date(startDate);
        const end = new Date(endDate);

        // Process each day in the date range
        for (
          let currentDate = new Date(start);
          currentDate <= end;
          currentDate.setDate(currentDate.getDate() + 1)
        ) {
          const dateStr = currentDate.toISOString().split("T")[0];
          const dayOfWeek = currentDate.getDay();
          const dayName = dayNames[dayOfWeek];

          // Check if caregiver has approved time off on this date
          const timeOffForDate = approvedTimeOff.find((timeOff) => {
            return dateStr >= timeOff.start_date && dateStr <= timeOff.end_date;
          });

          // Skip full day off - don't show any availability
          if (
            timeOffForDate &&
            timeOffForDate.request_type !== "single_partial_day"
          ) {
            continue;
          }

          // Find the schedule that applies to this specific date
          const applicableSchedule = schedules.find((schedule) => {
            const schedStart = schedule.start_date;
            const schedEnd = schedule.end_date;

            // Date must be >= start_date
            if (dateStr < schedStart) return false;

            // If there's an end_date, date must be <= end_date
            if (schedEnd && dateStr > schedEnd) return false;

            return true;
          });

          if (!applicableSchedule) continue;

          // Get the shifts for this day of the week
          const dayShifts = applicableSchedule[`${dayName}_shifts`] || [];

          // Create availability pseudo-shifts for each scheduled time slot
          dayShifts.forEach((scheduleShift, index) => {
            if (!scheduleShift.start || !scheduleShift.end) return;

            // Handle both "HH:MM" short format and full ISO datetime strings
            let startTime, endTime;
            if (scheduleShift.start.includes("T") || scheduleShift.start.length > 5) {
              const startUTC = new Date(scheduleShift.start);
              const endUTC = new Date(scheduleShift.end);
              startTime = `${String(startUTC.getHours()).padStart(2, "0")}:${String(startUTC.getMinutes()).padStart(2, "0")}`;
              endTime = `${String(endUTC.getHours()).padStart(2, "0")}:${String(endUTC.getMinutes()).padStart(2, "0")}`;
            } else {
              startTime = scheduleShift.start;
              endTime = scheduleShift.end;
            }

            // Array to hold the availability time slots (may be split by time off)
            const availabilitySlots = [];

            // If partial day time off, exclude the time off period from availability
            if (
              timeOffForDate &&
              timeOffForDate.request_type === "single_partial_day"
            ) {
              const timeOffStart = timeOffForDate.start_time;
              const timeOffEnd = timeOffForDate.end_time;

              // Check if scheduled time overlaps with time off
              if (startTime < timeOffEnd && endTime > timeOffStart) {
                // There's overlap - we need to split or skip this availability slot

                // If time off completely covers the scheduled time, skip this slot
                if (timeOffStart <= startTime && timeOffEnd >= endTime) {
                  return; // Skip this entire availability slot
                }

                // If time off covers the beginning - show availability after time off ends
                if (timeOffStart <= startTime && timeOffEnd < endTime) {
                  availabilitySlots.push({ start: timeOffEnd, end: endTime });
                }
                // If time off covers the end - show availability before time off starts
                else if (timeOffStart > startTime && timeOffEnd >= endTime) {
                  availabilitySlots.push({
                    start: startTime,
                    end: timeOffStart,
                  });
                }
                // Time off is in the middle - split into two availability slots
                else if (timeOffStart > startTime && timeOffEnd < endTime) {
                  availabilitySlots.push({
                    start: startTime,
                    end: timeOffStart,
                  });
                  availabilitySlots.push({ start: timeOffEnd, end: endTime });
                }
              } else {
                // No overlap with time off
                availabilitySlots.push({ start: startTime, end: endTime });
              }
            } else {
              // No partial time off for this date
              availabilitySlots.push({ start: startTime, end: endTime });
            }

            // Create availability pseudo-shifts for each slot (may be split by time off)
            availabilitySlots.forEach((slot, slotIndex) => {
              // Always show availability based on the caregiver's schedule
              // regardless of whether they have shifts assigned during this time
              availabilityShifts.push({
                id: `available_${caregiverId}_${dateStr}_${index}_${slotIndex}`,
                title: `Available - ${caregiverName}`,
                date: dateStr,
                startTime: slot.start,
                endTime: slot.end,
                formattedStartTime: this.formatTime(slot.start),
                formattedEndTime: this.formatTime(slot.end),
                client: "Available",
                caregiver: caregiverName,
                status: "available",
                statusClass: "status-available",
                notes: "Caregiver available for assignment",
                payer: "DSPD",
                shiftType: "1-on-1",
                originalData: scheduleShift,
                isAvailability: true,
              });
            });
          });
        }

        // Add availability slots to shifts array
        this.shifts = [...this.shifts, ...availabilityShifts];
      } catch (error) {
        this.$notifyError("Error loading caregiver availability: " + error);
      }
    },
    async fetchTeamAvailability(startDate, endDate, caregiversToFetch) {
      try {
        if (!caregiversToFetch || caregiversToFetch.length === 0) {
          this.teamViewCaregiverTimeOffs = {};
          return;
        }

        const formatDate = (date) => {
          if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}/.test(date)) {
            return date.split("T")[0];
          }
          if (date && typeof date.getFullYear === "function") {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
          }
          return null;
        };

        const formattedStart = formatDate(startDate);
        const formattedEnd = formatDate(endDate);
        if (!formattedStart || !formattedEnd) return;

        // Build a name lookup from already-loaded caregivers (no extra API calls needed)
        const nameMap = {};
        caregiversToFetch.forEach((c) => {
          nameMap[c.id] = c.name;
        });

        // Fetch all schedules in parallel
        const schedulePromises = caregiversToFetch.map((c) =>
          schedulesService.getByCaregiver(c.id)
            .then((resp) => ({ caregiverId: c.id, response: resp }))
            .catch(() => ({ caregiverId: c.id, response: null }))
        );
        const results = await Promise.all(schedulePromises);

        const dayNames = [
          "sunday", "monday", "tuesday", "wednesday",
          "thursday", "friday", "saturday",
        ];
        const allAvailabilityShifts = [];
        const timeOffs = {};

        for (const { caregiverId, response } of results) {
          if (!response) {
            timeOffs[caregiverId] = [];
            continue;
          }

          const schedules = (response.schedules || []).sort((a, b) => {
            if (a.start_date > b.start_date) return -1;
            if (a.start_date < b.start_date) return 1;
            return 0;
          });
          const approvedTimeOff = response.approvedTimeOff || [];
          timeOffs[caregiverId] = approvedTimeOff;

          if (schedules.length === 0) continue;

          const caregiverName = nameMap[caregiverId] || "Caregiver";
          const start = new Date(startDate);
          const end = new Date(endDate);

          for (
            let currentDate = new Date(start);
            currentDate <= end;
            currentDate.setDate(currentDate.getDate() + 1)
          ) {
            const dateStr = currentDate.toISOString().split("T")[0];
            const dayOfWeek = currentDate.getDay();
            const dayName = dayNames[dayOfWeek];

            const timeOffForDate = approvedTimeOff.find((t) =>
              dateStr >= t.start_date && dateStr <= t.end_date
            );

            if (timeOffForDate && timeOffForDate.request_type !== "single_partial_day") {
              continue;
            }

            const applicableSchedule = schedules.find((schedule) => {
              if (dateStr < schedule.start_date) return false;
              if (schedule.end_date && dateStr > schedule.end_date) return false;
              return true;
            });

            if (!applicableSchedule) continue;

            const dayShifts = applicableSchedule[`${dayName}_shifts`] || [];

            dayShifts.forEach((scheduleShift, index) => {
              if (!scheduleShift.start || !scheduleShift.end) return;

              // Handle both "HH:MM" short format and full ISO datetime strings
              let startTime, endTime;
              if (scheduleShift.start.includes("T") || scheduleShift.start.length > 5) {
                const startUTC = new Date(scheduleShift.start);
                const endUTC = new Date(scheduleShift.end);
                startTime = `${String(startUTC.getHours()).padStart(2, "0")}:${String(startUTC.getMinutes()).padStart(2, "0")}`;
                endTime = `${String(endUTC.getHours()).padStart(2, "0")}:${String(endUTC.getMinutes()).padStart(2, "0")}`;
              } else {
                startTime = scheduleShift.start;
                endTime = scheduleShift.end;
              }

              const availabilitySlots = [];

              if (timeOffForDate && timeOffForDate.request_type === "single_partial_day") {
                const timeOffStart = timeOffForDate.start_time;
                const timeOffEnd = timeOffForDate.end_time;
                if (startTime < timeOffEnd && endTime > timeOffStart) {
                  if (timeOffStart <= startTime && timeOffEnd >= endTime) return;
                  if (timeOffStart <= startTime && timeOffEnd < endTime) {
                    availabilitySlots.push({ start: timeOffEnd, end: endTime });
                  } else if (timeOffStart > startTime && timeOffEnd >= endTime) {
                    availabilitySlots.push({ start: startTime, end: timeOffStart });
                  } else if (timeOffStart > startTime && timeOffEnd < endTime) {
                    availabilitySlots.push({ start: startTime, end: timeOffStart });
                    availabilitySlots.push({ start: timeOffEnd, end: endTime });
                  }
                } else {
                  availabilitySlots.push({ start: startTime, end: endTime });
                }
              } else {
                availabilitySlots.push({ start: startTime, end: endTime });
              }

              availabilitySlots.forEach((slot, slotIndex) => {
                allAvailabilityShifts.push({
                  id: `available_${caregiverId}_${dateStr}_${index}_${slotIndex}`,
                  title: `Available - ${caregiverName}`,
                  date: dateStr,
                  startTime: slot.start,
                  endTime: slot.end,
                  formattedStartTime: this.formatTime(slot.start),
                  formattedEndTime: this.formatTime(slot.end),
                  client: "Available",
                  caregiver: caregiverName,
                  status: "available",
                  statusClass: "status-available",
                  notes: "Caregiver available for assignment",
                  payer: "DSPD",
                  shiftType: "1-on-1",
                  originalData: scheduleShift,
                  isAvailability: true,
                });
              });
            });
          }
        }

        // Batch-apply all results at once
        this.teamViewCaregiverTimeOffs = timeOffs;
        this.shifts = [...this.shifts, ...allAvailabilityShifts];
      } catch (error) {
        this.$notifyError("Error loading team availability: " + error);
      }
    },
    extractTimeFromDateTime(datetimeString) {
      if (!datetimeString) return "";
      // If it's already just a time (HH:MM or HH:MM:SS), return it
      if (!datetimeString.includes("T") && !datetimeString.includes(" ")) {
        return datetimeString.substring(0, 5); // Return HH:MM
      }
      // If it's a full datetime, extract the time part
      const timePart =
        datetimeString.split("T")[1] || datetimeString.split(" ")[1];
      if (timePart) {
        return timePart.substring(0, 5); // Return HH:MM
      }
      return "";
    },
    async loadAgencies() {
      try {
        const response = await officesService.list();
        this.availableAgencies = (response || []).map((office) => ({
          id: Number(office.id) || office.id,
          name: office.name,
        }));
      } catch (error) {
        this.$notifyError("Error loading agencies: " + error);
        this.availableAgencies = []; // Ensure it's always an array
      }
    },
    async loadAdmins() {
      try {
        const loginStore = useLoginStore();
        const userRole = loginStore.user?.user_type || "Admin";

        // Only Admins can see the admin filter
        if (userRole !== "Admin") {
          this.availableAdmins = [];
          return;
        }

        const response = await caregiversService.getAdmins();
        // Backend already returns formatted data with id and name
        this.availableAdmins = (response || []).map((admin) => ({
          id: Number(admin.id) || admin.id,
          name: admin.name,
        }));
      } catch (error) {
        this.$notifyError("Error loading admins: " + error);
        this.availableAdmins = []; // Ensure it's always an array
      }
    },
    getCalendarDateRange() {
      let startDate, endDate;

      if (this.currentView === "dayGridMonth") {
        // For month view, get the entire month being displayed
        const year = this.currentMonth.getFullYear();
        const month = this.currentMonth.getMonth();
        startDate = new Date(year, month, 1);
        endDate = new Date(year, month + 1, 0); // Last day of month
        endDate.setHours(23, 59, 59, 999); // Set to end of the last day
      } else if (this.currentView === "timeGridWeek") {
        // For week view, get the week being displayed
        const weekStart = new Date(this.dayViewDate[0]);
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        startDate = weekStart;
        endDate = weekEnd;
      } else if (this.currentView === "timeGridDay" || this.currentView === "teamView") {
        // For day view and team view, get just that day
        const day = new Date(this.dayViewDate[0]);
        day.setHours(0, 0, 0, 0);
        const dayEnd = new Date(day);
        dayEnd.setHours(23, 59, 59, 999);
        startDate = day;
        endDate = dayEnd;
      } else if (this.currentView === "customRange") {
        // For custom range view, use the selected range
        if (this.customRangeDays.length > 0) {
          startDate = new Date(this.customRangeDays[0]);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(this.customRangeDays[this.customRangeDays.length - 1]);
          endDate.setHours(23, 59, 59, 999);
        } else {
          // Fallback to today
          const now = new Date();
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          endDate = new Date(startDate);
          endDate.setHours(23, 59, 59, 999);
        }
      } else {
        // Default fallback
        const now = new Date();
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        endDate.setHours(23, 59, 59, 999); // Set to end of the last day
      }

      return {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      };
    },
    processShiftForCalendar(shift) {
      const clientName = this.formatClient(shift);
      const caregiverName = this.formatCaregiver(shift);
      const status = this.getShiftStatus(shift);
      const rawStartTime = shift.scheduled_start || shift.startTime;
      const rawEndTime = shift.scheduled_end || shift.endTime;

      // Convert UTC times to local times
      const { localStartTime, localEndTime } = this.convertUTCToLocal(
        shift.date,
        rawStartTime,
        rawEndTime,
      );

      // Get actual clock times if available (handle both snake_case and camelCase)
      let actualStartTime = null;
      let actualEndTime = null;
      const clockInTime = shift.clock_in || shift.clockIn;
      const clockOutTime = shift.clock_out || shift.clockOut;

      if (clockInTime) {
        const clockInDate = new Date(clockInTime);
        actualStartTime = `${String(clockInDate.getHours()).padStart(2, "0")}:${String(clockInDate.getMinutes()).padStart(2, "0")}`;
      }
      if (clockOutTime) {
        const clockOutDate = new Date(clockOutTime);
        actualEndTime = `${String(clockOutDate.getHours()).padStart(2, "0")}:${String(clockOutDate.getMinutes()).padStart(2, "0")}`;
      }

      // Determine which times to display as primary based on clock status
      let displayStartTime,
        displayEndTime,
        displayFormattedStartTime,
        displayFormattedEndTime;

      if (actualStartTime && actualEndTime) {
        // Clocked out: use actual times as primary display
        displayStartTime = actualStartTime;
        displayEndTime = actualEndTime;
        displayFormattedStartTime = this.formatTime(actualStartTime);
        displayFormattedEndTime = this.formatTime(actualEndTime);
      } else if (actualStartTime) {
        // Clocked in: use actual start time, scheduled end time
        displayStartTime = actualStartTime;
        displayEndTime = localEndTime;
        displayFormattedStartTime = this.formatTime(actualStartTime);
        displayFormattedEndTime = this.formatTime(localEndTime);
      } else {
        // Not clocked in: use scheduled times
        displayStartTime = localStartTime;
        displayEndTime = localEndTime;
        displayFormattedStartTime = this.formatTime(localStartTime);
        displayFormattedEndTime = this.formatTime(localEndTime);
      }

      return {
        id: shift.id,
        title: shift.group_name
          ? shift.group_name
          : `${clientName} - ${caregiverName}`,
        date: shift.date,
        startTime: displayStartTime,
        endTime: displayEndTime,
        scheduledStartTime: localStartTime,
        scheduledEndTime: localEndTime,
        actualStartTime: actualStartTime,
        actualEndTime: actualEndTime,
        formattedStartTime: displayFormattedStartTime,
        formattedEndTime: displayFormattedEndTime,
        formattedScheduledStartTime: this.formatTime(localStartTime),
        formattedScheduledEndTime: this.formatTime(localEndTime),
        formattedActualStartTime: actualStartTime
          ? this.formatTime(actualStartTime)
          : null,
        formattedActualEndTime: actualEndTime
          ? this.formatTime(actualEndTime)
          : null,
        client: clientName,
        caregiver: caregiverName,
        caregivers: shift.caregivers || [],
        clients: shift.clients || [],
        status: status || "scheduled",
        statusClass: this.getStatusClass(status),
        notes: shift.shift_notes || shift.notes || "",
        payer: shift.payer || "DSPD",
        shiftType: shift.shift_type || "1-on-1",
        groupName: shift.group_name || null,
        serviceCodes: shift.service_codes || [],
        nonServiceCodes: shift.non_service_codes || [],
        originalData: shift,
        hasClockIn: !!(shift.clock_in || shift.clockIn),
        hasClockOut: !!(shift.clock_out || shift.clockOut),
        // Recorded miles, viewer-scoped: caregivers see their own trip,
        // everyone else sees all trips recorded on the shift.
        miles:
          this.userRole === "Caregiver"
            ? milesForCaregiver(shift, useLoginStore().user?.id)
            : totalShiftMiles(shift),
      };
    },
    convertUTCToLocal(date, startTime, endTime) {
      // Handle new format where startTime/endTime are full UTC datetime strings
      let startDateTime, endDateTime;

      if (startTime && startTime.includes("T")) {
        // New format: full UTC datetime string
        startDateTime = new Date(startTime);
        endDateTime = new Date(endTime);
      } else {
        // Legacy format: date is in format YYYY-MM-DD, times are in format HH:MM or HH:MM:SS
        startDateTime = new Date(`${date}T${startTime}Z`); // Z indicates UTC
        endDateTime = new Date(`${date}T${endTime}Z`);
      }

      // Check if dates are valid
      if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
        return { localStartTime: startTime, localEndTime: endTime };
      }

      // Convert to local time and extract just the time portion
      const localStartHours = startDateTime.getHours();
      const localStartMinutes = startDateTime.getMinutes();
      const localEndHours = endDateTime.getHours();
      const localEndMinutes = endDateTime.getMinutes();

      // Format as HH:MM string
      const localStartTime = `${String(localStartHours).padStart(2, "0")}:${String(localStartMinutes).padStart(2, "0")}`;
      const localEndTime = `${String(localEndHours).padStart(2, "0")}:${String(localEndMinutes).padStart(2, "0")}`;

      return { localStartTime, localEndTime };
    },
    convertLocalToUTC(date, startTime, endTime) {
      // If startTime/endTime are already full datetime strings, return them as ISO strings
      if (startTime && startTime.includes("T")) {
        return {
          utcStartTime: new Date(startTime).toISOString(),
          utcEndTime: new Date(endTime).toISOString(),
        };
      }

      // Legacy format: date is in format YYYY-MM-DD, times are in format HH:MM or HH:MM:SS
      // Create local datetime by combining date and time
      const [year, month, day] = date.split("-").map(Number);
      const [startHours, startMinutes] = startTime.split(":").map(Number);
      const [endHours, endMinutes] = endTime.split(":").map(Number);

      // Create Date objects in local time
      const localStartDateTime = new Date(
        year,
        month - 1,
        day,
        startHours,
        startMinutes,
      );
      const localEndDateTime = new Date(
        year,
        month - 1,
        day,
        endHours,
        endMinutes,
      );

      // Return as full ISO datetime strings for the new format
      return {
        utcStartTime: localStartDateTime.toISOString(),
        utcEndTime: localEndDateTime.toISOString(),
      };
    },
    formatClient(shift) {
      // Show "N/A" for Non Service payer type shifts
      if (shift.payer === "Non Service") {
        return "N/A";
      }
      if (shift.clients && shift.clients.length > 1) {
        return shift.group_name || shift.groupName || `${shift.clients.length} clients`;
      }
      if (shift.clients && shift.clients.length > 0) {
        return shift.clients[0].name;
      }
      return "Unassigned";
    },
    formatCaregiver(shift) {
      const caregivers = shift.caregivers || [];
      if (caregivers.length > 1) {
        // Non-group shifts with shadow/training caregivers show the
        // primary plus a compact count of the extras
        if ((shift.shift_type || "1-on-1") !== "Group") {
          const primary = primaryCaregiverOf(shift);
          const extras = caregivers.length - 1;
          return `${primary.name} +${extras} shadow`;
        }
        return shift.group_name || shift.groupName || `${caregivers.length} caregivers`;
      }
      if (caregivers.length > 0) {
        return caregiverNameWithRole(caregivers[0]);
      }
      return "Unassigned";
    },
    getShiftStatus(shift) {
      if (shift.isPotential) {
        return "scheduled";
      }

      // For group shifts, check if the current user is an assigned caregiver
      // Use shift_type === 'Group' (not isDSPDGroupShift) so this works even without service_codes
      if (shift.shift_type === 'Group' && shift.caregivers && shift.caregivers.length > 0) {
        const isAssigned = shift.caregivers.some(c => Number(c.id) === Number(this.userId));
        if (isAssigned) {
          // Show the current user's own individual status from caregiverClockTimes
          const clockTimes = shift.caregiverClockTimes || [];
          const myClockTime = clockTimes.find(ct => Number(ct.id_users) === Number(this.userId));
          const overallStatus = (shift.shift_status || shift.status || '').toLowerCase();
          const shiftDate = (shift.date || shift.scheduled_date || '').split('T')[0];
          const today = new Date();
          const todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
          const dayPast = shiftDate && shiftDate < todayStr;

          if (!myClockTime || !myClockTime.clock_in) {
            if (overallStatus === 'approved') return 'approved';
            if (overallStatus === 'missed') return 'missed';
            if (dayPast) return 'missed';
            return 'scheduled';
          } else if (myClockTime.clock_in && !myClockTime.clock_out) {
            if (dayPast) return 'error';
            return 'clocked-in';
          } else if (myClockTime.clock_out) {
            if (overallStatus === 'approved') return 'approved';
            return 'clocked-out';
          }
          return 'scheduled';
        }
        // Not assigned - for DSPD group shifts, derive overall status from all caregivers
        if (isDSPDGroupShift(shift)) {
          const statuses = shift.caregivers.map(c => getCaregiverShiftStatus(shift, c.id));
          if (statuses.some(s => s === 'error')) return 'error';
          if (statuses.every(s => s === 'approved')) return 'approved';
          if (statuses.every(s => s === 'missed')) return 'missed';
          if (statuses.every(s => s === 'missed' || s === 'error')) return 'error';
          if (statuses.some(s => s === 'clocked-in')) return 'clocked-in';
          if (statuses.some(s => s === 'clocked-out')) return 'clocked-out';
          return 'scheduled';
        }
      }

      // Check the actual status value
      const status = shift.shift_status || shift.status;
      if (status) {
        if (status.toLowerCase() === "unassigned") return "unassigned";
        if (status.toLowerCase() === "approved") return "approved";
        if (status.toLowerCase() === "clocked out") return "clocked-out";
        if (status.toLowerCase() === "clocked in") return "clocked-in";
        if (status.toLowerCase() === "missed") return "missed";
        if (status.toLowerCase() === "error") return "error";
        if (status.toLowerCase() === "canceled") return "canceled";
      }

      // No status set — check if the shift date has passed
      const shiftDate = (shift.date || shift.scheduled_date || '').split('T')[0];
      if (shiftDate) {
        const today = new Date();
        const todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
        if (shiftDate < todayStr) {
          if (!shift.clockIn && !shift.clock_in) {
            return 'missed';
          }
          return 'error';
        }
      }

      // Check if no caregiver is assigned
      const hasCaregivers = shift.caregivers && shift.caregivers.length > 0;
      const hasCaregiver = shift.caregiver && shift.caregiver !== "Unassigned";
      if (
        !hasCaregivers &&
        !hasCaregiver &&
        (!status || status.toLowerCase() === "scheduled")
      ) {
        return "unassigned";
      }

      if (shift.clockOut) {
        return "clocked-out";
      }
      if (shift.clockIn) {
        return "clocked-in";
      }

      return "scheduled";
    },
    getStatusClass(status) {
      const statusClasses = {
        unassigned: "status-unassigned",
        scheduled: "status-scheduled",
        "clocked-in": "status-clocked-in",
        "clocked-out": "status-clocked-out",
        approved: "status-approved",
        available: "status-available",
        missed: "status-missed",
        error: "status-error",
        canceled: "status-canceled",
      };
      return statusClasses[status] || "status-scheduled";
    },
    formatTime(timeString) {
      if (!timeString) return "";

      // Handle both HH:MM and HH:MM:SS formats
      const timeParts = timeString.split(":");
      let hours = parseInt(timeParts[0]);
      const minutes = timeParts[1] || "00";

      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12; // 0 should be 12

      // Remove leading zero from minutes if present, but keep two digits for formatting
      const formattedMinutes = minutes === "00" ? "00" : minutes;

      return `${hours}:${formattedMinutes}${ampm}`;
    },
    formatShiftTooltip(shift) {
      let tooltip = ``;

      // Show primary display times first (actual or scheduled)
      if (shift.hasClockIn || shift.hasClockOut) {
        const displayStart = shift.formattedStartTime;
        const displayEnd = shift.formattedEndTime;

        if (shift.hasClockOut) {
          tooltip += `Actual: ${displayStart} - ${displayEnd}`;
          // Calculate hours worked from actual times
          const actualStart = shift.actualStartTime || shift.formattedStartTime;
          const actualEnd = shift.actualEndTime || shift.formattedEndTime;
          if (actualStart && actualEnd) {
            const startParts = actualStart.split(':').map(Number);
            const endParts = actualEnd.split(':').map(Number);
            let startMin = startParts[0] * 60 + startParts[1];
            let endMin = endParts[0] * 60 + endParts[1];
            if (endMin < startMin) endMin += 24 * 60; // overnight
            const totalMin = endMin - startMin;
            const hrs = Math.floor(totalMin / 60);
            const mins = totalMin % 60;
            tooltip += `\nHours Worked: ${hrs}:${String(mins).padStart(2, '0')}`;
          }
        } else {
          tooltip += `Actual: ${displayStart} - In Progress`;
        }

        // Show scheduled times as reference
        const scheduledStart = shift.formattedScheduledStartTime;
        const scheduledEnd = shift.formattedScheduledEndTime;
        tooltip += `\nScheduled: ${scheduledStart} - ${scheduledEnd}`;
        {
          const sParts = shift.scheduledStartTime.split(':').map(Number);
          const eParts = shift.scheduledEndTime.split(':').map(Number);
          let sMin = sParts[0] * 60 + sParts[1];
          let eMin = eParts[0] * 60 + eParts[1];
          if (eMin < sMin) eMin += 24 * 60;
          const total = eMin - sMin;
          tooltip += `\nHours Scheduled: ${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
        }
      } else {
        // Show only scheduled times when not clocked in
        const scheduledStart = shift.formattedStartTime;
        const scheduledEnd = shift.formattedEndTime;
        tooltip += `Scheduled: ${scheduledStart} - ${scheduledEnd}`;
        {
          const sParts = shift.startTime.split(':').map(Number);
          const eParts = shift.endTime.split(':').map(Number);
          let sMin = sParts[0] * 60 + sParts[1];
          let eMin = eParts[0] * 60 + eParts[1];
          if (eMin < sMin) eMin += 24 * 60;
          const total = eMin - sMin;
          tooltip += `\nHours Scheduled: ${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
        }
      }

      // Display all names if there are multiple clients or caregivers
      const clientDisplay = shift.clients && shift.clients.length > 1
        ? shift.clients.map(c => c.name).join(', ')
        : shift.client;
      let caregiverDisplay;
      if (isDSPDGroupShift(shift.originalData) && (this.userRole === 'Admin' || this.userRole === 'Lead')) {
        caregiverDisplay = shift.caregivers.map(caregiver => {
          const status = getCaregiverShiftStatus(shift.originalData, caregiver.id);
          const statusIcon = getStatusIcon(status);
          return `${statusIcon} ${caregiver.name}`;
        }).join(', ');
      } else {
        caregiverDisplay = shift.caregivers && shift.caregivers.length > 1
          ? shift.caregivers.map(c => caregiverNameWithRole(c)).join(', ')
          : shift.caregiver;
      }

      const clientLabel = shift.clients && shift.clients.length > 1 ? 'Clients' : 'Client';
      const caregiverLabel = shift.caregivers && shift.caregivers.length > 1 ? 'Caregivers' : 'Caregiver';

      if (shift.groupName) {
        tooltip += `\nGroup: ${shift.groupName}`;
      }
      tooltip += `\n${clientLabel}: ${clientDisplay}
${caregiverLabel}: ${caregiverDisplay}
Payer: ${shift.payer}
Shift Type: ${shift.shiftType}
Status: ${shift.status}`;

      const cancelReason = shift.originalData?.cancel_reason;
      if (cancelReason) {
        tooltip += `\nCanceled: ${cancelReason}`;
      }

      const notes = shift.notes || shift.originalData?.shift_notes;
      if (notes) {
        tooltip += `\nNotes: ${notes}`;
      }

      return tooltip;
    },
    getOrdinal(n) {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return s[(v - 20) % 10] || s[v] || s[0];
    },
    isToday(dateObj) {
      const now = new Date();
      return (
        dateObj.getFullYear() === now.getFullYear() &&
        dateObj.getMonth() === now.getMonth() &&
        dateObj.getDate() === now.getDate()
      );
    },
    updateDaysInMonth() {
      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth();
      const arr = [];
      const first = new Date(year, month, 1);
      const firstWeekday = first.getDay();
      for (let i = 0; i < firstWeekday; i++) {
        arr.push(new Date(year, month, i - firstWeekday + 1));
      }
      const d = new Date(year, month, 1);
      while (d.getMonth() === month) {
        arr.push(new Date(d));
        d.setDate(d.getDate() + 1);
      }
      const total = arr.length;
      const trailing = (7 - (total % 7)) % 7;
      for (let i = 1; i <= trailing; i++) {
        arr.push(new Date(year, month + 1, i));
      }
      this.daysInMonth = arr;
    },
    goToPreviousMonth() {
      const nm = new Date(this.currentMonth);
      nm.setMonth(nm.getMonth() - 1);
      this.currentMonth = nm;
      this.updateDaysInMonth();
    },
    goToNextMonth() {
      const nm = new Date(this.currentMonth);
      nm.setMonth(nm.getMonth() + 1);
      this.currentMonth = nm;
      this.updateDaysInMonth();
    },
    goToPrevious() {
      if (this.currentView === "dayGridMonth") {
        this.goToPreviousMonth();
        this.selectedDate = new Date(this.currentMonth);
      } else {
        const step = this.currentView === "timeGridWeek" ? 7 : 1;
        const nd = new Date(this.dayViewDate[0]);
        nd.setDate(nd.getDate() - step);
        this.dayViewDate = [nd];
        this.selectedDate = new Date(nd);
      }
      this.$nextTick(() => {
        this.updateDatePickerDisplay();
      });
    },
    goToNext() {
      if (this.currentView === "dayGridMonth") {
        this.goToNextMonth();
        this.selectedDate = new Date(this.currentMonth);
      } else {
        const step = this.currentView === "timeGridWeek" ? 7 : 1;
        const nd = new Date(this.dayViewDate[0]);
        nd.setDate(nd.getDate() + step);
        this.dayViewDate = [nd];
        this.selectedDate = new Date(nd);
      }
      this.$nextTick(() => {
        this.updateDatePickerDisplay();
      });
    },
    handleTodayClick() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      this.selectedDate = new Date(today);

      let message = "";

      if (this.currentView === "dayGridMonth") {
        this.currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        this.updateDaysInMonth();
        message = "Navigated to the month containing today's date";
      } else if (this.currentView === "timeGridWeek") {
        const sow = new Date(today);
        sow.setDate(today.getDate() - today.getDay());
        this.dayViewDate = [sow];
        message = "Navigated to the week containing today's date";
      } else if (this.currentView === "timeGridDay" || this.currentView === "teamView") {
        this.dayViewDate = [today];
        message = "Navigated to today's date";
      } else if (this.currentView === "customRange") {
        // Reset custom range to current week
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);

        const formatDate = (d) => {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${y}-${m}-${day}`;
        };

        this.customDateRange = [formatDate(startOfWeek), formatDate(endOfWeek)];
        this.customRangeDays = this.generateDateRange(startOfWeek, endOfWeek);
        this.fetchShifts();
        message = "Set custom range to the week containing today";
      }

      this.todayClicked = true;
      setTimeout(() => (this.todayClicked = false), 1000);

      this.$notifySuccess(message);
    },
    // Handle custom date range selection
    handleCustomDateRangeChange(dateRange) {
      if (!dateRange || dateRange.length !== 2) {
        this.customRangeDays = [];
        return;
      }
      const [startStr, endStr] = dateRange;
      const startDate = this.parseLocalDate(startStr);
      const endDate = this.parseLocalDate(endStr);
      this.customRangeDays = this.generateDateRange(startDate, endDate);
      this.fetchShifts();
    },
    // Parse YYYY-MM-DD string to local Date
    parseLocalDate(dateStr) {
      const [year, month, day] = dateStr.split("-").map(Number);
      return new Date(year, month - 1, day);
    },
    // Generate array of dates between start and end (inclusive)
    generateDateRange(startDate, endDate) {
      const dates = [];
      const current = new Date(startDate);
      current.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(0, 0, 0, 0);
      while (current <= end) {
        dates.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      return dates;
    },
    // Build grid for MonthView with proper week alignment
    calculateCustomRangeGrid() {
      if (this.customRangeDays.length === 0) return [];
      const arr = [];
      const firstDay = this.customRangeDays[0];

      // Add leading padding to align with weekday
      const firstWeekday = firstDay.getDay();
      for (let i = 0; i < firstWeekday; i++) {
        const paddingDate = new Date(firstDay);
        paddingDate.setDate(firstDay.getDate() - (firstWeekday - i));
        arr.push(paddingDate);
      }

      // Add all days in range
      this.customRangeDays.forEach((date) => arr.push(new Date(date)));

      // Add trailing padding to complete final week
      const trailing = (7 - (arr.length % 7)) % 7;
      const lastDay = this.customRangeDays[this.customRangeDays.length - 1];
      for (let i = 1; i <= trailing; i++) {
        const paddingDate = new Date(lastDay);
        paddingDate.setDate(lastDay.getDate() + i);
        arr.push(paddingDate);
      }
      return arr;
    },
    handleDateChange() {
      if (this.selectedDate) {
        let newDate = this.selectedDate;

        // Handle different date formats properly
        if (typeof newDate === "string") {
          if (newDate.includes("-")) {
            const [year, month, day] = newDate.split("-").map(Number);
            newDate = new Date(year, month - 1, day);
          } else {
            newDate = new Date(newDate);
          }
        } else if (!(newDate instanceof Date)) {
          newDate = new Date(newDate);
        }

        // Validate the date
        if (isNaN(newDate.getTime())) {
          return;
        }

        // Set hours to avoid timezone issues
        newDate.setHours(0, 0, 0, 0);

        if (this.currentViewSelect === "dayGridMonth") {
          this.currentMonth = new Date(
            newDate.getFullYear(),
            newDate.getMonth(),
            1,
          );
          this.updateDaysInMonth();
        } else if (this.currentViewSelect === "timeGridWeek") {
          const sow = new Date(newDate);
          sow.setDate(newDate.getDate() - newDate.getDay());
          this.dayViewDate = [sow];
        } else if (this.currentViewSelect === "timeGridDay" || this.currentViewSelect === "teamView") {
          this.dayViewDate = [new Date(newDate)];
        }

        // Ensure shifts are refetched with the new date
        this.fetchShifts();
      }
    },
    handleViewChange(newView) {
      // If already on this view, don't re-initialize dates
      if (this.currentView === newView) {
        return;
      }

      this.currentView = newView;
      this.currentViewSelect = newView;

      // When switching views, default to the period that includes today
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (newView === "timeGridDay") {
        this.dayViewDate = [new Date(today)];
        this.selectedDate = new Date(today);
      } else if (newView === "timeGridWeek") {
        const sow = new Date(today);
        sow.setDate(today.getDate() - today.getDay());
        this.dayViewDate = [sow];
        this.selectedDate = new Date(sow);
      } else if (newView === "dayGridMonth") {
        this.currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        this.updateDaysInMonth();
        this.selectedDate = new Date(this.currentMonth);
      } else if (newView === "teamView") {
        // Team view uses day navigation like day view
        this.dayViewDate = [new Date(today)];
        this.selectedDate = new Date(today);
      } else if (newView === "customRange") {
        // Clear custom date range - wait for user to select
        this.customDateRange = null;
        this.customRangeDays = [];
      }

      // Update date picker display (fetchShifts will be triggered by dayViewDate/currentMonth watchers)
      this.$nextTick(() => {
        this.updateDatePickerDisplay();
      });
    },
    switchToMonth() {
      if (this.currentView === "dayGridMonth") return;
      let y, m;
      if (
        this.currentView === "timeGridWeek" ||
        this.currentView === "timeGridDay"
      ) {
        const d = this.dayViewDate[0] || new Date();
        y = d.getFullYear();
        m = d.getMonth();
      } else {
        y = this.currentMonth.getFullYear();
        m = this.currentMonth.getMonth();
      }
      this.currentMonth = new Date(y, m, 1);
      this.updateDaysInMonth();
      this.currentView = "dayGridMonth";
      this.currentViewSelect = "dayGridMonth";
    },
    switchToWeek() {
      if (this.currentView === "timeGridWeek") return;
      let anchor;
      if (this.currentView === "dayGridMonth") {
        anchor = new Date(
          this.currentMonth.getFullYear(),
          this.currentMonth.getMonth(),
          1,
        );
      } else if (this.currentView === "timeGridDay") {
        anchor = this.dayViewDate[0] || new Date();
      } else {
        anchor = new Date();
      }
      const d = new Date(anchor);
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - d.getDay());
      this.dayViewDate = [d];
      this.currentView = "timeGridWeek";
      this.currentViewSelect = "timeGridWeek";
    },
    switchToDay() {
      if (this.currentView === "timeGridDay") return;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let target;
      if (this.currentView === "dayGridMonth") {
        target = new Date(
          this.currentMonth.getFullYear(),
          this.currentMonth.getMonth(),
          1,
        );
      } else if (this.currentView === "timeGridWeek") {
        const focused = this.dayViewDate[0] || new Date();
        const ws = new Date(focused);
        ws.setHours(0, 0, 0, 0);
        ws.setDate(focused.getDate() - focused.getDay());
        const we = new Date(ws);
        we.setDate(ws.getDate() + 6);
        we.setHours(0, 0, 0, 0);
        if (today >= ws && today <= we) {
          target = today;
        } else {
          target = ws;
        }
      } else {
        target = this.dayViewDate[0] || today;
      }
      this.dayViewDate = [target];
      this.currentView = "timeGridDay";
      this.currentViewSelect = "timeGridDay";
    },
    viewShiftDetails(shift) {
      this.selectedShift = shift;
      this.selectedShiftForActions = shift;

      // Canceled shifts are always view-only
      if (shift.status === "canceled") {
        this.isViewOnlyMode = true;
        this.showShiftActionModal = true;
        return;
      }

      // Check if shift is approved - if so, show Shift Details modal instead of Edit
      const isShiftApproved = shift.status === "approved";

      // Check if shift is missed - if so, show action modal to allow approval
      const isShiftMissed = shift.status === "missed";

      // Check if user can clock in, clock out, or edit
      const canClockIn = this.canUserClockIntoShift(shift);
      const canClockOut = this.canUserClockOutOfShift(shift);
      const canEdit = this.canUserEditShift();

      // If shift is approved, show the action modal (allows Undo Approval for Admins)
      if (isShiftApproved) {
        this.isViewOnlyMode = false;
        this.showShiftActionModal = true;
        return;
      }

      // If shift is missed, show action modal (admin/lead will see approve button)
      if (isShiftMissed) {
        this.isViewOnlyMode = false;
        this.showShiftActionModal = true;
        return;
      }

      // If user can't clock in but can edit, go straight to edit
      // EXCEPT for:
      // 1. clocked-in, clocked-out, or error shifts where admins should see action modal for time editing
      // 2. Employment specialists who can clock in (they should see the modal with both Edit and Clock In options)
      const isShiftClockedIn =
        shift.status === "clocked-in" || shift.status === "clocked in";
      const isShiftClockedOut =
        shift.status === "clocked-out" || shift.status === "clocked out";
      const isShiftError = shift.status === "error";

      // For Employment Specialists: if they can clock in, always show action modal (not direct edit)
      // This allows them to choose between Edit and Clock In actions
      const isEmploymentSpecialist = this.userRole === "Employment Specialist";
      const shouldShowModalForES = isEmploymentSpecialist && canClockIn;

      // For Admins/Leads assigned as caregivers, show the action modal
      // so they can clock in instead of going straight to edit
      const isAssignedCaregiver = (shift.originalData?.caregivers || shift.caregivers || []).some(
        (c) => Number(c.id) === Number(this.userId)
      );

      if (
        !canClockIn &&
        !canClockOut &&
        canEdit &&
        !isShiftClockedIn &&
        !isShiftClockedOut &&
        !isShiftError &&
        !shouldShowModalForES &&
        !isAssignedCaregiver
      ) {
        this.editShift(shift);
        return;
      }

      // If user can't clock in, can't clock out, and can't edit, show view-only modal
      // Exception: assigned caregivers should still get the action modal so ShiftActionModal
      // can evaluate its own canClockIntoShift logic (which may differ from the Calendar's check)
      if (!canClockIn && !canClockOut && !canEdit && !isAssignedCaregiver) {
        this.isViewOnlyMode = true;
        this.showShiftActionModal = true;
        return;
      }

      // Reset view-only mode for normal cases (user can perform some action)
      this.isViewOnlyMode = false;

      // Show the action modal which will determine available actions
      this.showShiftActionModal = true;
    },
    canUserEditShift() {
      const role = this.userRole;
      return (
        role === "Admin" || role === "Lead" || role === "Employment Specialist"
      );
    },
    canUserClockIntoShift(shift) {
      if (!shift.id) return false;

      const status = shift.status;
      const isLinkedToUser = shift.originalData?.caregivers?.some(
        (c) =>
          Number(c.id) === Number(this.userId) ||
          String(c.id) === String(this.userId),
      );

      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      const shiftDateStr = shift.date.split("T")[0];
      const isToday = todayStr === shiftDateStr;

      const now = new Date();
      const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();

      const startTime =
        shift.originalData?.scheduled_start || shift.startTime || "";
      const endTime = shift.originalData?.scheduled_end || shift.endTime || "";

      if (startTime && endTime) {
        const convertUTCTimeToLocalMinutes = (timeStr, dateStr) => {
          // Handle full ISO datetime strings (e.g., "2025-09-23T13:00:00.000Z")
          if (timeStr && timeStr.includes('T')) {
            const utcDate = new Date(timeStr);
            if (!isNaN(utcDate.getTime())) {
              return utcDate.getHours() * 60 + utcDate.getMinutes();
            }
          }

          // Handle time-only strings (e.g., "13:00:00")
          const utcDateTime = `${dateStr}T${timeStr}Z`;
          const utcDate = new Date(utcDateTime);
          if (!isNaN(utcDate.getTime())) {
            return utcDate.getHours() * 60 + utcDate.getMinutes();
          }
          const [hour, min] = timeStr.split(":").map(Number);
          return hour * 60 + min;
        };

        const startMinutes = convertUTCTimeToLocalMinutes(
          startTime,
          shiftDateStr,
        );
        const endMinutes = convertUTCTimeToLocalMinutes(endTime, shiftDateStr);
        // CMP exception: allow clock-in any time during the calendar day
        const isCMPShift = (shift.originalData?.service_codes || shift.service_codes || [])
          .some(sc => sc.code === 'CMP');

        const allowedStartMinutes = startMinutes - 30; // 30 minutes before

        const isInTimeWindow = isCMPShift
          ? true
          : (currentTimeMinutes >= allowedStartMinutes &&
             currentTimeMinutes < endMinutes);

        // For DSPD group shifts, check caregiver-specific status
        // (overall shift status may differ when other caregivers have clocked in)
        const shiftData = shift.originalData || shift;
        let isScheduledStatus;
        if (isDSPDGroupShift(shiftData)) {
          const caregiverStatus = getCaregiverShiftStatus(shiftData, this.userId);
          isScheduledStatus = caregiverStatus === 'scheduled';
        } else {
          isScheduledStatus = status === "scheduled";
        }

        return (
          isScheduledStatus && isLinkedToUser && isToday && isInTimeWindow
        );
      }

      return false;
    },
    canUserClockOutOfShift(shift) {
      if (!shift.id) return false;

      // Check both status and shift_status fields
      const status = shift.status || shift.originalData?.shift_status;
      const isLinkedToUser = shift.originalData?.caregivers?.some(
        (c) =>
          Number(c.id) === Number(this.userId) ||
          String(c.id) === String(this.userId),
      );

      // Handle various status formats for clocked in
      const normalizedStatus = status
        ? status.toLowerCase().replace(/\s+/g, "-")
        : "";
      const isClockedIn =
        normalizedStatus === "clocked-in" || status === "Clocked In";

      // Only allow clock out if shift is clocked in and user is the assigned caregiver
      return isClockedIn && isLinkedToUser;
    },
    async editShift(shift) {
      // Convert the calendar shift format to the format expected by ShiftModal
      // Use the original UTC times from the database, not the converted local times
      const shiftToEdit = {
        ...shift.originalData,
        id: shift.id,
        date: shift.date,
        scheduled_start:
          shift.originalData.scheduled_start || shift.originalData.startTime,
        scheduled_end:
          shift.originalData.scheduled_end || shift.originalData.endTime,
        shift_type: shift.shiftType,
        payer: shift.payer,
        shift_notes: shift.notes,
        shift_status:
          shift.originalData.shift_status ||
          shift.originalData.status ||
          shift.status,
        clients: shift.originalData.clients || [],
        caregivers: shift.originalData.caregivers || [],
        isPotential: shift.originalData.isPotential || false,
        isAvailability: shift.isAvailability || false,
      };

      // Don't allow editing of availability slots
      if (shift.isAvailability) {
        this.$notifyInfo(
          "Caregiver availability slots cannot be edited directly",
        );
        return;
      }

      const status = (
        shift.status ||
        shift.shift_status ||
        shift.originalData?.shift_status
      )?.toLowerCase();

      // For group shifts, admins edit caregiver assignments via the clock time modals
      const isGroupShift = shiftToEdit.shift_type === "Group" || shift.shiftType === "Group";
      const isClocked = status === "clocked-in" || status === "clocked in";
      if (isGroupShift && this.userRole === "Admin") {
        if (isClocked) {
          this.shiftToEditClockInTime = shiftToEdit;
          this.showEditClockInTimeModal = true;
          return;
        }
        if (status === "clocked-out" || status === "clocked out") {
          this.handleEditClockOutTime(shift);
          return;
        }
      }

      // Handle clocked-out/error shifts - route to EditShiftNotesModal for assigned caregivers
      const isClockedOut = status === "clocked-out" || status === "clocked out" || status === "error";
      if (isClockedOut) {
        const mappedShift = {
          ...shiftToEdit,
          startTime: shiftToEdit.scheduled_start,
          endTime: shiftToEdit.scheduled_end,
          originalData: {
            ...shift.originalData,
            scheduled_start: shiftToEdit.scheduled_start,
            scheduled_end: shiftToEdit.scheduled_end,
          },
        };

        // Check if the current user is the assigned caregiver
        const caregivers = shiftToEdit.caregivers || [];
        const isAssignedCaregiver = caregivers.some(
          (c) => Number(c.id) === Number(this.userId)
        );

        // Admins/Leads who are NOT assigned use EditClockOutTimeModal
        if (
          (this.userRole === "Admin" || this.userRole === "Lead") &&
          !isAssignedCaregiver
        ) {
          this.handleEditClockOutTime(shift);
          return;
        }

        // Everyone else (caregivers, ES, assigned admins) edits notes
        this.shiftToEditNotes = mappedShift;
        this.showEditShiftNotesModal = true;
        return;
      }

      const isMissed = status === "missed";

      // Handle missed shifts
      if (isMissed) {
        const mappedShift = {
          ...shiftToEdit,
          startTime: shiftToEdit.scheduled_start,
          endTime: shiftToEdit.scheduled_end,
          originalData: {
            ...shift.originalData,
            scheduled_start: shiftToEdit.scheduled_start,
            scheduled_end: shiftToEdit.scheduled_end,
          },
        };

        // Check if the current user is the assigned caregiver
        const caregivers = shiftToEdit.caregivers || [];
        const assignedCaregiver = caregivers.length > 0 ? caregivers[0] : null;
        const isAssignedCaregiver =
          assignedCaregiver && assignedCaregiver.id === Number(this.userId);

        // Caregivers and Employment Specialists can only edit notes and activities
        if (
          this.userRole === "Caregiver" ||
          this.userRole === "Employment Specialist"
        ) {
          this.shiftToEditNotes = mappedShift;
          this.showEditShiftNotesModal = true;
          return;
        }

        // Admins and Leads who are assigned to the shift can only edit notes and activities
        if (
          (this.userRole === "Admin" || this.userRole === "Lead") &&
          isAssignedCaregiver
        ) {
          this.shiftToEditNotes = mappedShift;
          this.showEditShiftNotesModal = true;
          return;
        }

        // Admins and Leads who are NOT assigned can edit the full shift details
        if (
          (this.userRole === "Admin" || this.userRole === "Lead") &&
          !isAssignedCaregiver
        ) {
          // Check if the assigned caregiver is an admin
          let hasAdminCaregiver = false;
          if (shiftToEdit.caregivers && shiftToEdit.caregivers.length > 0) {
            const caregiverType = shiftToEdit.caregivers[0].user_type;
            hasAdminCaregiver =
              caregiverType === "Admin" || caregiverType === "Lead";
          }

          // If the shift has an admin caregiver, reload caregivers with admins included
          if (hasAdminCaregiver) {
            await this.loadCaregivers(true);
            this.shiftBeingEdited = shiftToEdit;
            await this.$nextTick();
            this.showShiftModal = true;
          } else {
            this.shiftBeingEdited = shiftToEdit;
            this.showShiftModal = true;
          }
          return;
        }
      }

      // Check if the assigned caregiver is an admin
      let hasAdminCaregiver = false;
      if (shiftToEdit.caregivers && shiftToEdit.caregivers.length > 0) {
        const caregiverType = shiftToEdit.caregivers[0].user_type;
        hasAdminCaregiver =
          caregiverType === "Admin" || caregiverType === "Lead";
      }

      // If the shift has an admin caregiver, reload caregivers with admins included
      if (hasAdminCaregiver) {
        await this.loadCaregivers(true);
        // Set the shift data first so the modal can access it
        this.shiftBeingEdited = shiftToEdit;
        // Wait for Vue to update the DOM
        await this.$nextTick();
        // Now show the modal
        this.showShiftModal = true;
      } else {
        // For non-admin caregivers, proceed normally
        this.shiftBeingEdited = shiftToEdit;
        this.showShiftModal = true;
      }
    },
    async handleShiftClockIn(shift) {
      // Nudge toward the mobile app before opening the web clock-in flow.
      if (!(await this.$clockNudge("in"))) return;
      // Use originalData if available, which contains the full shift details including authorization
      this.currentShift = shift.originalData || shift;
      this.selectedGroupClients = [];

      this.showClockInModal = true;
    },

    async handleShiftClockOut(shift) {
      if (!(await this.$clockNudge("out"))) return;
      // Use originalData if available, which contains the full shift details including authorization
      this.currentShift = shift.originalData || shift;
      this.showClockOutModal = true;
    },

    handleAdminClockOut(shift) {
      this.shiftToAdminClockOut = shift;
      this.showAdminClockOutModal = true;
    },

    handleShiftStartBreak(shift) {
      // Use originalData if available, which contains the full shift details including authorization
      this.currentShift = shift.originalData || shift;
      this.showStartBreakModal = true;
    },

    onBreakActionSuccess(response) {
      // Instantly update the isOnBreak flag based on the response
      if (this.currentShift && response && response.break) {
        // If breakEnd is null, the break was started; otherwise it was ended
        this.currentShift.isOnBreak = response.break.breakEnd === null;

        // Also update the shift in the shifts array
        const shiftIndex = this.shifts.findIndex(s => s.id === this.currentShift.id);
        if (shiftIndex !== -1) {
          this.shifts[shiftIndex].isOnBreak = response.break.breakEnd === null;
        }
      }

      // Refresh shifts after break action
      this.fetchShifts();
    },

    handleShiftEdit(shift) {
      this.editShift(shift);
    },

    handleEditShiftNotes(shift) {
      const originalData = shift.originalData || shift;
      const mappedShift = {
        ...originalData,
        id: shift.id,
        date: shift.date,
        startTime: originalData.scheduled_start,
        endTime: originalData.scheduled_end,
        originalData: {
          ...originalData,
          scheduled_start: originalData.scheduled_start,
          scheduled_end: originalData.scheduled_end,
        },
      };
      this.shiftToEditNotes = mappedShift;
      this.showEditShiftNotesModal = true;
    },

    handleEditClockInTime(shift) {
      // Use originalData if available, which contains the full shift details
      this.shiftToEditClockInTime = shift.originalData || shift;
      this.showEditClockInTimeModal = true;
    },

    handleEditClockOutTime(shift) {
      // Create a properly structured shift object that includes both original data and processed times
      const originalData = shift.originalData || shift;
      const mappedShift = {
        ...originalData,
        // Include actual times from Calendar processing if available
        actualStartTime: shift.actualStartTime,
        actualEndTime: shift.actualEndTime,
        // Ensure clock times are available in multiple formats for compatibility
        clockIn: originalData.clock_in || originalData.clockIn,
        clock_in: originalData.clock_in || originalData.clockIn,
        clockOut: originalData.clock_out || originalData.clockOut,
        clock_out: originalData.clock_out || originalData.clockOut,
        // Preserve scheduled times
        startTime: shift.scheduledStartTime || originalData.scheduled_start,
        endTime: shift.scheduledEndTime || originalData.scheduled_end,
        scheduled_start: originalData.scheduled_start,
        scheduled_end: originalData.scheduled_end,
        // Ensure group shift fields are preserved
        clientDetails: originalData.clientDetails,
        caregiverClockTimes: originalData.caregiverClockTimes,
        caregivers: originalData.caregivers,
        service_codes: originalData.service_codes,
        // Store original data for reference
        originalData: originalData,
      };

      this.shiftToEditClockOutTime = mappedShift;
      this.showEditClockOutTimeModal = true;
    },

    handleApproveShift(shift) {
      this.shiftToApprove = {
        ...shift,
        startTime: shift.startTime || shift.originalData?.scheduled_start,
        endTime: shift.endTime || shift.originalData?.scheduled_end,
        // Preserve service code data from originalData
        service_codes: shift.originalData?.service_codes || [],
        // Preserve all JC-related activity data
        jc_primary_activities: shift.originalData?.jc_primary_activities,
        jc_secondary_activities: shift.originalData?.jc_secondary_activities,
        jc_shift_notes: shift.originalData?.jc_shift_notes,
        // Preserve other service activity data
        ls_activities: shift.originalData?.ls_activities,
        ls_shift_notes_summary: shift.originalData?.ls_shift_notes_summary,
        sed_activities: shift.originalData?.sed_activities,
        sed_shift_notes_summary: shift.originalData?.sed_shift_notes_summary,
        sb_activities: shift.originalData?.sb_activities,
        jd_activity: shift.originalData?.jd_activity,
        jd_outcome: shift.originalData?.jd_outcome,
        jd_next_steps: shift.originalData?.jd_next_steps,
        // Preserve group shift data
        caregiverClockTimes: shift.originalData?.caregiverClockTimes || [],
        clientDetails: shift.originalData?.clientDetails || [],
        originalData: {
          ...shift.originalData,
          clockIn: shift.originalData?.clockIn || shift.originalData?.clock_in,
          clock_in: shift.originalData?.clockIn || shift.originalData?.clock_in,
          clockOut:
            shift.originalData?.clockOut || shift.originalData?.clock_out,
          clock_out:
            shift.originalData?.clockOut || shift.originalData?.clock_out,
        },
      };
      this.showApproveShiftModal = true;
    },
    async handleUndoApproval(shift) {
      try {
        // Confirm the action
        const confirmed = await this.$confirm(
          'Are you sure you want to undo this shift approval? This will restore hours back to the authorization and revert the shift to "Clocked Out" status.',
          "Confirm Undo Approval",
          {
            confirmButtonText: "Yes, Undo Approval",
            cancelButtonText: "Cancel",
            type: "warning",
          },
        );

        if (!confirmed) {
          return;
        }

        // Call the undo approval API
        await shiftsService.undoShiftApproval({
          shiftId: shift.originalData?.id || shift.id,
        });

        this.$notifySuccess(
          "Shift approval undone successfully! Authorization hours have been restored.",
        );

        // Close the modal and reload shifts
        this.showShiftActionModal = false;
        await this.fetchShifts();
      } catch (error) {
        if (error !== "cancel") {
          this.$notifyError(
            error.response?.data?.error || "Failed to undo shift approval",
          );
        }
      }
    },
    handleApproveMissedShift(shift) {
      // Map the shift data for the approve missed shift modal
      this.shiftToApproveMissed = {
        ...shift,
        scheduledStartTime:
          shift.startTime || shift.originalData?.scheduled_start,
        scheduledEndTime: shift.endTime || shift.originalData?.scheduled_end,
        startTime: shift.startTime || shift.originalData?.scheduled_start,
        endTime: shift.endTime || shift.originalData?.scheduled_end,
        // Preserve service code data from originalData
        service_codes: shift.originalData?.service_codes || [],
        originalData: {
          ...shift.originalData,
          scheduled_start: shift.originalData?.scheduled_start,
          scheduled_end: shift.originalData?.scheduled_end,
        },
      };
      this.showApproveMissedShiftModal = true;
    },
    async handleCancelShift(shift) {
      const original = shift.originalData || shift;
      const shiftId = shift.id || original.id;
      if (!shiftId) return;

      const isRecurring =
        original.recurring ||
        original.parent_recurring_shift_id ||
        shift.recurring ||
        shift.parent_recurring_shift_id;

      // For a single (non-materialized) occurrence of a recurring series the id
      // we hold is the recurring TEMPLATE id, so we must tell the API which date
      // and scope to act on — otherwise the whole series gets canceled.
      let cancelTargetId = shiftId;
      let cancelExtra = {};
      if (isRecurring) {
        let scope;
        try {
          await this.$confirm(
            "This is a recurring shift. Cancel only this occurrence, or the entire series?",
            "Cancel Recurring Shift",
            {
              confirmButtonText: "Only This Occurrence",
              cancelButtonText: "Entire Series",
              distinguishCancelAndClose: true,
              type: "warning",
            },
          );
          scope = "occurrence";
        } catch (action) {
          if (action === "cancel") {
            scope = "series";
          } else {
            return; // dialog dismissed
          }
        }

        if (scope === "occurrence") {
          const shiftDate = shift.date
            ? shift.date.split("T")[0]
            : original.date
              ? original.date.split("T")[0]
              : null;
          if (!shiftDate) {
            this.$notifyError(
              "Could not determine the occurrence date to cancel.",
            );
            return;
          }
          cancelExtra = { date: shiftDate };
        } else {
          // Cancel the whole series — target the recurring template.
          cancelTargetId =
            original.parent_recurring_shift_id ||
            shift.parent_recurring_shift_id ||
            original.original_recurring_shift_id ||
            shiftId;
          cancelExtra = { cancelEntireSeries: true };
        }
      }

      try {
        await this.$prompt(
          "Please provide a reason for canceling this shift:",
          "Cancel Shift",
          {
            confirmButtonText: "Cancel Shift",
            cancelButtonText: "Go Back",
            inputType: "textarea",
            inputPlaceholder: "e.g., client canceled, caregiver sick, weather...",
            inputValidator: (val) => {
              if (!val || !val.trim()) return "A cancellation reason is required";
              return true;
            },
            beforeClose: async (action, instance, done) => {
              if (action !== "confirm") {
                done();
                return;
              }
              const reason = instance.inputValue;
              if (!reason || !reason.trim()) return;

              instance.confirmButtonLoading = true;
              instance.confirmButtonText = "Canceling...";
              try {
                await shiftsService.update(cancelTargetId, {
                  shift_status: "Canceled",
                  adminChangeReason: reason.trim(),
                  ...cancelExtra,
                });
                this.$notifySuccess("Shift canceled successfully");
                done();
                await this.fetchShifts();
              } catch (error) {
                instance.confirmButtonLoading = false;
                instance.confirmButtonText = "Cancel Shift";
                this.$notifyError(
                  error.response?.data?.message || "Error canceling shift",
                );
              }
            },
          },
        );
      } catch {
        // User clicked "Go Back"
      }
    },
    // Delete triggered from the edit modal: close it first, then run the
    // standard delete flow (confirm dialog or recurring options dialog).
    handleEditModalDelete(shift) {
      this.closeShiftModal();
      this.handleDeleteShift(shift);
    },
    // Delete a shift from the calendar, mirroring the shifts page behavior:
    // recurring shifts get the 3-option dialog, others a simple confirm.
    async handleDeleteShift(shift) {
      const original = shift.originalData || shift;
      const isRecurring =
        original.recurring ||
        original.parent_recurring_shift_id ||
        shift.recurring ||
        shift.parent_recurring_shift_id;

      try {
        if (isRecurring) {
          this.deleteRecurringDialog = { visible: true, shift };
        } else {
          const confirmDelete = await this.$confirmAction(
            null,
            "Are you sure you want to delete this shift?",
          );
          if (!confirmDelete) return;
          await shiftsService.delete(shift.id);
          this.$notifySuccess("Shift deleted successfully!");
          await this.fetchShifts();
        }
      } catch (error) {
        this.$notifyError("Error deleting shift: " + error);
      }
    },
    async deleteThisShiftOnly() {
      const shift = this.deleteRecurringDialog.shift;
      this.deleteRecurringDialog = { visible: false, shift: null };
      try {
        const shiftDate = shift.date ? shift.date.split("T")[0] : null;
        await shiftsService.deleteWithDate(shift.id, shiftDate);
        this.$notifySuccess("Shift occurrence deleted successfully!");
        await this.fetchShifts();
      } catch (error) {
        this.$notifyError("Error deleting shift: " + error);
      }
    },
    async deleteThisAndFutureShifts() {
      const shift = this.deleteRecurringDialog.shift;
      this.deleteRecurringDialog = { visible: false, shift: null };
      try {
        const original = shift.originalData || shift;
        const parentId =
          original.parent_recurring_shift_id ||
          shift.parent_recurring_shift_id ||
          shift.id;
        const shiftDate = shift.date ? shift.date.split("T")[0] : null;
        await shiftsService.deleteFromDate(parentId, shiftDate);
        this.$notifySuccess("This and all future occurrences deleted successfully!");
        await this.fetchShifts();
      } catch (error) {
        this.$notifyError("Error deleting shifts: " + error);
      }
    },
    async deleteAllShifts() {
      const shift = this.deleteRecurringDialog.shift;
      this.deleteRecurringDialog = { visible: false, shift: null };
      try {
        const original = shift.originalData || shift;
        const parentId =
          original.parent_recurring_shift_id ||
          shift.parent_recurring_shift_id ||
          shift.id;
        await shiftsService.deleteEntireSeries(parentId);
        this.$notifySuccess("Entire recurring series deleted successfully!");
        await this.fetchShifts();
      } catch (error) {
        this.$notifyError("Error deleting series: " + error);
      }
    },
    computeShiftStyle(shift) {
      const [sh, sm] = shift.startTime.split(":").map(Number);
      const [eh, em] = shift.endTime.split(":").map(Number);
      const start = sh * 60 + sm;
      const end = eh * 60 + em;

      // Separate availability slots from regular shifts
      const dateShifts = this.shifts
        .filter((s) => s.date === shift.date)
        .map((s) => {
          const [oh, om] = s.startTime.split(":").map(Number);
          const [oh2, om2] = s.endTime.split(":").map(Number);
          return { ...s, _start: oh * 60 + om, _end: oh2 * 60 + om2 };
        });

      // Check if there's availability for this date
      const hasAvailability = dateShifts.some((s) => s.isAvailability);

      const lanes = [];
      let laneIndex = 0;

      // If this is an availability slot, always put it in lane 0
      if (shift.isAvailability) {
        laneIndex = 0;
      } else {
        // For regular shifts, calculate lanes excluding availability slots
        const regularShifts = dateShifts.filter((s) => !s.isAvailability);
        regularShifts.forEach((s) => {
          let i = lanes.findIndex((lane) =>
            lane.every((x) => x._end <= s._start),
          );
          if (i === -1) {
            lanes.push([]);
            i = lanes.length - 1;
          }
          lanes[i].push(s);
          if (s.id === shift.id) laneIndex = i;
        });
      }

      // Check if there's time off for this date
      const timeOffForDate = this.selectedCaregiverApprovedTimeOff.find(
        (timeOff) => {
          return (
            shift.date >= timeOff.start_date && shift.date <= timeOff.end_date
          );
        },
      );
      const hasFullDayTimeOff =
        timeOffForDate && timeOffForDate.request_type !== "single_partial_day";

      // Calculate lane offset:
      // - Add 1 if there's full-day time off (full-day time off bar at top)
      // - Add 1 if there's availability/partial-day time off and this is NOT an availability slot
      //   (availability and partial-day time off share the same lane since they don't overlap)
      let laneOffset = hasFullDayTimeOff ? 1 : 0;
      if (hasAvailability && !shift.isAvailability) {
        laneOffset += 1;
      } else if (
        timeOffForDate &&
        timeOffForDate.request_type === "single_partial_day" &&
        !shift.isAvailability
      ) {
        laneOffset += 1;
      }

      // We have 17 time slot headers (6AM through 10PM inclusive)
      // Time slots are displayed as flexbox items with equal widths
      // Each hour slot takes up 1/17th of the container width
      const totalSlots = 17; // 6AM through 10PM inclusive

      const startHour = sh;
      const endHour = eh;
      const startMinutes = sm;
      const endMinutes = em;

      // Calculate which slot index each time falls into (6AM = 0, 7AM = 1, etc.)
      const startSlotIndex = startHour - 6; // 6AM = 0, 7AM = 1, etc.
      const endSlotIndex = endHour - 6;

      // Position is based on slot index plus fractional minutes within that slot
      const startPosition = startSlotIndex + startMinutes / 60;
      const endPosition = endSlotIndex + endMinutes / 60;

      // Convert to percentages - each slot is 1/17th of the width
      const startPercent = Math.max((startPosition / totalSlots) * 100, 0);
      const endPercent = Math.min((endPosition / totalSlots) * 100, 100);
      const widthPercent = Math.max(endPercent - startPercent, 1);

      return {
        left: `${startPercent}%`,
        width: `${widthPercent}%`,
        top: `${(laneIndex + laneOffset) * 28}px`, // Increased spacing for taller shifts
        height: "24px",
        position: "absolute",
        color: "#fff",
        fontSize: "0.75rem",
        borderRadius: "4px",
      };
    },
    dateOfWeek(dayOffset) {
      const sunday = new Date(this.dayViewDate[0]);
      sunday.setDate(sunday.getDate() + dayOffset);
      return sunday;
    },
    shiftsForDate(dateObj) {
      const y = dateObj.getFullYear();
      const m = String(dateObj.getMonth() + 1).padStart(2, "0");
      const d = String(dateObj.getDate()).padStart(2, "0");
      const dateStr = `${y}-${m}-${d}`;

      let dayShifts = this.shifts.filter((shift) => shift.date === dateStr);

      // When filtering by specific caregivers in Day/Week/Team view:
      // Always show their availability, their assigned shifts, and all unassigned shifts
      if (
        this.selectedCaregiver &&
        this.selectedCaregiver.length > 0 &&
        (this.currentView === "timeGridDay" ||
          this.currentView === "timeGridWeek" ||
          this.currentView === "teamView")
      ) {
        // If a status filter is applied, show:
        // 1. Shifts matching the status filter
        // 2. ALWAYS show availability (status="available")
        // 3. ALWAYS show unassigned shifts (status="unassigned")
        // 4. ALWAYS show missed shifts without caregivers (status="missed")
        if (this.selectedShiftStatus) {
          dayShifts = dayShifts.filter(
            (shift) =>
              shift.status === this.selectedShiftStatus ||
              shift.status === "available" ||
              shift.status === "unassigned" ||
              shift.status === "missed",
          );
        }
        // If no status filter, show everything (which is already in dayShifts)
      } else if (this.selectedShiftStatus === "unassigned") {
        // When filtering by "Unassigned" status (and not filtering by caregiver),
        // show unassigned shifts, missed shifts without caregivers, and available caregiver slots
        dayShifts = dayShifts.filter(
          (shift) =>
            shift.status === "unassigned" ||
            shift.status === "available" ||
            shift.status === "missed",
        );
      }

      // All other filtering is now handled by the backend API
      // No need for frontend filtering of caregiver, client, payer, shiftType, agency, or admin

      return dayShifts;
    },
    createShift() {
      this.shiftBeingEdited = null;
      this.showShiftModal = true;
    },
    closeShiftModal() {
      this.showShiftModal = false;
      this.shiftBeingEdited = null;
    },
    async saveShift({ shift, shifts, isEditing, isGroupShift, isDSPDGroupShift, exceptionShiftIds }) {
      try {
        // Helper to convert times for a single shift object
        const convertShiftTimes = (s) => {
          let dateToUse = null;
          if (s.recurring) {
            dateToUse = s.recurring_start_date;
          } else {
            dateToUse = s.date ? s.date.split("T")[0] : null;
          }
          const { utcStartTime, utcEndTime } = this.convertLocalToUTC(
            dateToUse,
            s.scheduled_start,
            s.scheduled_end,
          );
          return {
            ...s,
            scheduled_start: utcStartTime,
            scheduled_end: utcEndTime,
          };
        };

        // Handle DSPD group shifts (single shift with multiple clients and task assignments)
        if (isDSPDGroupShift && shift && !isEditing) {
          const processedShift = convertShiftTimes(shift);
          await shiftsService.create(processedShift);

          const clientCount = shift.clientIds?.length || 1;
          this.$notifySuccess(`DSPD group shift created successfully for ${clientCount} client(s)!`);
          this.closeShiftModal();
          await this.fetchShifts();
          this.$root.$emit("shift-saved");
          return;
        }

        // Handle group shifts (multiple shifts to create)
        if (isGroupShift && shifts && Array.isArray(shifts)) {
          const createdShifts = [];
          for (const shiftData of shifts) {
            const processedShift = convertShiftTimes(shiftData);
            const savedShift = await shiftsService.create(processedShift);
            createdShifts.push(savedShift);
          }

          this.$notifySuccess(`${createdShifts.length} group shifts created successfully!`);
          this.closeShiftModal();
          await this.fetchShifts();
          this.$root.$emit("shift-saved");
          return;
        }

        // Preserve existing status if it's "missed", otherwise default to "Scheduled"
        let shiftStatus = "Scheduled";
        if (isEditing && this.shiftBeingEdited) {
          const currentStatus =
            this.shiftBeingEdited.shift_status || this.shiftBeingEdited.status;
          if (currentStatus && currentStatus.toLowerCase() === "missed") {
            shiftStatus = "Missed";
          } else if (currentStatus && currentStatus !== "Scheduled") {
            // Preserve other statuses like "Approved", "Clocked In", "Clocked Out", etc.
            shiftStatus = currentStatus;
          }
        }

        // Handle date for single vs recurring shifts
        let dateToUse = null;
        if (shift.recurring) {
          // For recurring shifts, use the start date for time conversion
          dateToUse = shift.recurring_start_date;
        } else {
          // For single shifts, use the shift date
          dateToUse = shift.date ? shift.date.split("T")[0] : null;
        }

        // Convert local times back to UTC before saving
        const { utcStartTime, utcEndTime } = this.convertLocalToUTC(
          dateToUse,
          shift.scheduled_start,
          shift.scheduled_end,
        );

        // Start with all shift data to preserve fields like editEntireSeriesFromDate
        const shiftData = {
          ...shift,
          // Override specific fields
          scheduled_start: utcStartTime,
          scheduled_end: utcEndTime,
          shift_status: shiftStatus,
          clientIds:
            shift.clientIds || (shift.clientId ? [shift.clientId] : []),
          caregiverIds:
            shift.caregiverIds ||
            (shift.caregiverId ? [shift.caregiverId] : []),
          serviceCodeIds: shift.serviceCodeIds || [],
        };

        // Add date only for single shifts
        if (!shift.recurring && shift.date) {
          shiftData.date = shift.date.split("T")[0];
        }

        // Include client address ID if provided
        if (shift.clientAddressId) {
          shiftData.clientAddressId = shift.clientAddressId;
        }

        // Include clientGeocode if provided
        if (shift.clientGeocode) {
          shiftData.clientGeocode = shift.clientGeocode;
        }

        // Include clientGeocodeAddressId if provided (which address the geocode is for)
        if (shift.clientGeocodeAddressId) {
          shiftData.clientGeocodeAddressId = shift.clientGeocodeAddressId;
        }

        // Add recurring shift data if applicable
        if (shift.recurring) {
          shiftData.recurring = true;
          shiftData.recurrencePattern = shift.recurrencePattern;
          shiftData.selectedWeekdays = shift.selectedWeekdays || [];
          shiftData.customDates = shift.customDates || [];
          shiftData.recurring_start_date = shift.recurring_start_date;
          shiftData.recurring_end_date = shift.recurring_end_date || null;
        }

        // Include VR alternate location data if present
        if (shift.vrAlternateLocation) {
          shiftData.vrAlternateLocation = shift.vrAlternateLocation;
          shiftData.useAlternateLocation = shift.useAlternateLocation;
        }

        let savedShift;
        if (isEditing && !shift.isPotential) {
          savedShift = await shiftsService.update(shift.id, shiftData);
          this.$notifySuccess("Shift updated successfully!");
        } else if (isEditing && shift.isPotential) {
          savedShift = await shiftsService.create(shiftData);
          await this.recordDeletedPotentialShift(this.shiftBeingEdited);
          this.$notifySuccess("Shift updated successfully!");
        } else {
          savedShift = await shiftsService.create(shiftData);
          this.$notifySuccess("Shift created successfully!");
        }

        // If the scheduler chose to keep individually-changed occurrences, move
        // them onto the new series the edit created (a series edit splits the
        // old series and starts a new one; exceptions stay tied to the old one
        // until re-parented).
        if (exceptionShiftIds && exceptionShiftIds.length > 0 && savedShift) {
          try {
            const newRecurringShiftId =
              savedShift.newRecurringShiftId || savedShift.id;
            if (newRecurringShiftId) {
              await shiftsService.handleExceptionShifts({
                action: "keep",
                exceptionShiftIds,
                newRecurringShiftId,
              });
              this.$notifySuccess("Changed occurrences kept on the new series");
            }
          } catch (exceptionError) {
            this.$notifyError(
              "Shift saved, but the changed occurrences could not be moved to the new series",
            );
          }
        }

        this.closeShiftModal();
        await this.fetchShifts();

        // Emit global event to notify TopNavbar to refresh
        this.$root.$emit("shift-saved");
      } catch (error) {
        // Client overlap is overridable: offer "Schedule anyway" and retry
        // with the acknowledgement flag (managers get notified server-side).
        // Caregiver availability is overridable too, with a confirm that
        // states the payroll/report consequences before the retry.
        if (await this.confirmCaregiverOverride(error)) {
          if (shift) shift.acknowledgeCaregiverConflict = true;
          (shifts || []).forEach((s) => {
            if (s) s.acknowledgeCaregiverConflict = true;
          });
          return this.saveShift({
            shift,
            shifts,
            isEditing,
            isGroupShift,
            isDSPDGroupShift,
          });
        }
        if (await this.confirmClientOverlap(error)) {
          if (shift) shift.acknowledgeClientOverlap = true;
          (shifts || []).forEach((s) => {
            if (s) s.acknowledgeClientOverlap = true;
          });
          return this.saveShift({
            shift,
            shifts,
            isEditing,
            isGroupShift,
            isDSPDGroupShift,
          });
        }
        // Rich, itemized overlap message when conflict detail is present.
        this.handleShiftSaveError(error);
      }
    },
    async recordDeletedPotentialShift(potentialShift) {
      try {
        const deletedShiftData = {
          date: potentialShift.date.split("T")[0],
          scheduled_start: potentialShift.startTime,
          scheduled_end: potentialShift.endTime,
          client_id:
            potentialShift.clients && potentialShift.clients.length > 0
              ? potentialShift.clients[0].id
              : null,
          caregiver_id:
            potentialShift.caregivers && potentialShift.caregivers.length > 0
              ? potentialShift.caregivers[0].id
              : null,
          shift_type: potentialShift.shift_type,
          payer: potentialShift.payer,
          notes: `Converted to actual shift on calendar`,
        };

        await deletedPotentialShiftsService.create(deletedShiftData);
      } catch (error) {
        this.$notifyError("Error recording deleted potential shift");
      }
    },
    async loadCaregivers(includeAdmins = false) {
      try {
        const loginStore = useLoginStore();
        const userRole = loginStore.user?.user_type || "Admin";
        const userId = loginStore.user?.id || "1";

        let caregivers = [];

        if (userRole === "Admin" || userRole === "Lead") {
          const params = new URLSearchParams();
          if (includeAdmins) {
            params.append("includeAdmins", "true");
          }
          const response = await caregiversService.list(params);
          caregivers = response || [];
        } else if (userRole === "Employment Specialist") {
          // Employment Specialists can only see themselves in caregiver dropdowns
          const params = new URLSearchParams();
          if (includeAdmins) {
            params.append("includeAdmins", "true");
          }
          const response = await caregiversService.list(params);
          const allCaregivers = response || [];
          caregivers = allCaregivers.filter((c) => c.id === userId);
        }

        // Backend now handles the filtering based on includeAdmins parameter
        this.availableCaregivers = caregivers.map((caregiver) => ({
          id: Number(caregiver.id) || caregiver.id,
          name: `${caregiver.first_name} ${caregiver.last_name}`,
          user_type: caregiver.user_type,
          id_admin: caregiver.id_admin ? (Number(caregiver.id_admin) || caregiver.id_admin) : null,
        }));
      } catch (error) {
        this.$notifyError("Error loading caregivers: " + error);
        this.availableCaregivers = []; // Ensure it's always an array
      }
    },
    async loadClients() {
      try {
        const loginStore = useLoginStore();
        const userRole = loginStore.user?.user_type || "Admin";

        if (userRole === "Admin" || userRole === "Lead") {
          const response = await clientsService.list({ slim: true });
          this.availableClients = (response || []).map((client) => ({
            id: Number(client.id) || client.id,
            name: `${client.first_name} ${client.last_name}`,
          }));
        }
      } catch (error) {
        this.$notifyError("Error loading clients: " + error);
        this.availableClients = []; // Ensure it's always an array
      }
    },
    handleOpenGroupTasks(shift) {
      this.groupTasksShift = shift.originalData || shift;
      this.showGroupTasksModal = true;
    },

    async handleClockInSuccess(response) {
      await this.fetchShifts();
      // Emit global event to notify TopNavbar to refresh
      this.$root.$emit("shift-saved");
    },

    async handleClockOutSuccess(response) {
      await this.fetchShifts();
      // Emit global event to notify TopNavbar to refresh
      this.$root.$emit("shift-saved");
    },
  },
};
</script>

<style scoped>
.extended-calendar {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: transparent;
  color: #fff;
}
.ec-views {
  flex: 1;
  padding: 0.5rem 0 1rem 0;
}
.ec-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 1rem;
}
.ec-nav-month {
  display: flex;
  flex-direction: row;
  align-items: center;
}
.month-year {
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 0.5rem;
}
.ec-nav-month .nav-arrow {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #fff;
  cursor: pointer;
  padding: 0 0.25rem;
}
.ec-nav-month .nav-arrow.right {
  margin-left: 0.25rem;
}
.ec-nav-month .month-name {
  font-size: 2rem;
  font-weight: bold;
  margin: 0 0.5rem;
  color: #fff;
}
.year-label {
  font-size: 2rem;
  color: #ccc;
  font-weight: 300;
  margin: 0.1rem 0 0;
}
.ec-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
  width: 100%;
}
.view-links {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9rem;
  margin-left: auto;
}
.view-links a {
  text-decoration: none;
  color: #ccc;
  cursor: pointer;
}
.view-links a.active {
  color: #6ba6cd;
  font-weight: bold;
}
.view-links .separator {
  color: #ccc;
  margin: 0 0.5rem;
}
@media (max-width: 600px) {
  .ec-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  .ec-nav-month {
    width: 100%;
    justify-content: space-between;
  }
  .ec-actions {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  .view-links {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  .month-year {
    flex-direction: row;
    align-items: baseline;
    justify-content: center;
    gap: 0.25rem;
  }
  .month-name {
    font-size: 1.25rem !important;
  }
}

/* Legend Row within Compact Filter Section */
.filter-row-compact.legend-row {
  justify-content: center;
}

.legend-groups {
  display: flex;
  align-items: flex-start;
  gap: 2rem;
  flex-wrap: wrap;
  justify-content: center;
}

.legend-group {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.legend-group-header {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #6ba6cd;
  letter-spacing: 0.025em;
  text-transform: uppercase;
  flex-shrink: 0;
}

.legend-group-header i {
  font-size: 0.75rem;
  width: 12px;
  text-align: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.9);
  padding: 0.25rem 0;
  transition: color 0.2s ease;
}

.legend-item:hover {
  color: #fff;
}

.legend-color {
  width: 14px;
  height: 10px;
  border-radius: 2px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;
}

.legend-item:hover .legend-color {
  transform: scale(1.1);
}

.legend-divider {
  width: 1px;
  height: 24px;
  background: linear-gradient(
    to bottom,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  flex-shrink: 0;
}

/* Status legend colors - muted but distinct */
.legend-color.status-unassigned {
  background: linear-gradient(
    135deg,
    rgba(32, 201, 151, 0.7),
    rgba(32, 201, 151, 0.5)
  ); /* Teal for unassigned shifts */
}

.legend-color.status-scheduled {
  background: linear-gradient(
    135deg,
    rgba(13, 110, 253, 0.8),
    rgba(13, 110, 253, 0.6)
  ); /* Bright blue for scheduled */
}

.legend-color.status-clocked-in {
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.9),
    rgba(255, 193, 7, 0.7)
  ); /* Bright yellow for clocked in */
}

.legend-color.status-clocked-out {
  background: linear-gradient(
    135deg,
    rgba(237, 137, 54, 0.7),
    rgba(237, 137, 54, 0.5)
  ); /* Muted orange */
}

.legend-color.status-approved {
  background: linear-gradient(
    135deg,
    rgba(156, 39, 176, 0.7),
    rgba(156, 39, 176, 0.5)
  ); /* Muted deep purple */
}

.legend-color.status-available {
  background: rgba(64, 158, 255, 0.1);
  border: 1px solid rgba(64, 158, 255, 0.3);
}

.legend-color.status-missed {
  background: linear-gradient(
    135deg,
    rgba(108, 117, 125, 0.8),
    rgba(108, 117, 125, 0.6)
  ); /* Grey for missed shifts */
}

.legend-color.status-error {
  background: linear-gradient(
    135deg,
    rgba(220, 53, 69, 0.8),
    rgba(220, 53, 69, 0.6)
  ); /* Red for error status */
}

/* Payer and shift type legend colors are now defined in global shift-colors.css */

/* Responsive Legend Design */
@media (max-width: 768px) {
  .modern-legend {
    margin: 0 0.5rem 1rem 0.5rem;
    padding: 0.75rem 1rem;
  }

  .legend-groups {
    gap: 1.5rem;
  }

  .legend-group {
    gap: 0.75rem;
  }
}

@media (max-width: 480px) {
  .legend-groups {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .legend-group {
    justify-content: center;
    gap: 0.5rem;
  }

  .legend-divider {
    display: none;
  }
}

/* Compact Filter Section */
.compact-filter-section {
  --primary-color: #6ba6cd;
  --primary-hover: #5a94b8;
  --background-glass: rgba(255, 255, 255, 0.08);
  --border-color: rgba(255, 255, 255, 0.12);
  --text-primary: #fff;
  --text-secondary: rgba(255, 255, 255, 0.7);
  --shadow-subtle: 0 2px 8px rgba(0, 0, 0, 0.1);

  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  padding: 0;
}

/* Filter Rows */
.filter-row-compact {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 1rem;
  background: var(--background-glass);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: var(--shadow-subtle);
  transition: all 0.2s ease;
  flex-wrap: wrap;
}

.filter-row-compact:hover {
  border-color: rgba(255, 255, 255, 0.18);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
}

.filter-row-compact.navigation-row {
  justify-content: center;
}

.filter-row-compact.data-row {
  justify-content: center;
}

.filter-group-compact {
  display: flex;
  align-items: center;
  min-width: 0;
  flex-shrink: 0;
}

.filter-group-compact.date-group {
  min-width: 320px;
  flex-grow: 1;
  max-width: 480px;
}

/* Prev/next arrows live inside the date group, flanking the picker */
.filter-group-compact.date-nav-group {
  gap: 0.5rem;
}

.date-nav-group .compact-date-picker {
  flex: 1;
  min-width: 0;
}

/* Compact Select Styling */
.compact-select {
  min-width: 120px;
}

:deep(.compact-select .el-input__inner) {
  background: rgba(255, 255, 255, 0.06) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 6px !important;
  color: var(--text-primary) !important;
  height: 40px !important;
  font-size: 13px !important;
  transition: all 0.2s ease !important;
  padding: 0 12px !important;
}

:deep(.compact-select .el-input__inner:hover) {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
}

:deep(.compact-select .el-input__inner:focus) {
  background: rgba(255, 255, 255, 0.12) !important;
  border-color: var(--primary-color) !important;
  box-shadow: 0 0 0 2px rgba(107, 166, 205, 0.1) !important;
}

:deep(.compact-select .el-input__inner::placeholder) {
  color: rgba(255, 255, 255, 0.5) !important;
  font-size: 12px !important;
}

/* Compact Date Picker */
.compact-date-picker {
  width: 100%;
}

::v-deep .compact-date-picker .el-input__inner {
  text-overflow: clip;
  white-space: nowrap;
}

/* Date Range Picker Styling */
::v-deep .el-date-editor {
  width: 100%;
}

::v-deep .el-date-editor .el-input__inner {
  background-color: #656565 !important;
  border: 1px solid #4a5568;
  color: #ffffff;
}

::v-deep .el-date-editor .el-input__inner:focus {
  border-color: #4caf50;
}

::v-deep .el-date-editor .el-input__icon {
  color: #a0aec0;
}

::v-deep .el-date-editor--daterange {
  width: 100% !important;
}

::v-deep .el-date-editor--daterange .el-input__suffix {
  right: 10px !important;
}

::v-deep .el-range-separator {
  color: #ffffff !important;
  padding: 0 5px !important;
}

::v-deep .el-range-input {
  background-color: #656565 !important;
  color: #ffffff !important;
}

::v-deep .el-range__close-icon {
  right: 10px !important;
}

::v-deep .el-date-editor--daterange .el-range__icon {
  color: #a0aec0;
}

/* Compact Navigation Button */
.compact-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.8);
  padding: 0;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 40px;
  width: 40px;
  min-width: 40px;
}

.compact-nav-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.25);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.compact-nav-btn:active {
  transform: translateY(0);
}

.compact-nav-btn i {
  font-size: 12px;
}

/* Compact Today Button */
.compact-today-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  background: linear-gradient(
    135deg,
    var(--primary-color),
    var(--primary-hover)
  );
  border: none;
  border-radius: 6px;
  color: white;
  padding: 0 0.75rem;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(205, 144, 189, 0.2);
  height: 40px;
  white-space: nowrap;
  min-width: 70px;
}

.mileage-total-chip {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  height: 40px;
  padding: 0 0.75rem;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.compact-today-btn:hover {
  background: linear-gradient(135deg, var(--primary-hover), #a67491);
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(205, 144, 189, 0.3);
}

.compact-today-btn:active {
  transform: translateY(0);
}

.compact-today-btn i {
  font-size: 0.75rem;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .filter-row-compact {
    gap: 0.75rem;
  }

  .compact-select {
    min-width: 110px;
  }
}

@media (max-width: 768px) {
  .compact-filter-section {
    padding: 0 0.5rem;
    gap: 0.5rem;
  }

  .filter-row-compact {
    gap: 0.5rem;
    padding: 0.6rem 0.75rem;
  }

  /* Navigation: row 1 = [view select][Today], row 2 = [<][date][>] */
  .filter-row-compact.navigation-row {
    flex-wrap: wrap;
  }

  .navigation-row .filter-group-compact.view-group {
    order: 1;
    flex: 1 1 0%;
    min-width: 0;
  }

  .navigation-row .filter-group-compact.today-group {
    order: 2;
    flex: 0 0 auto;
  }

  .navigation-row .filter-group-compact.date-group {
    order: 3;
    flex: 1 1 100%;
    min-width: 0;
    max-width: unset;
  }

  .view-group .compact-select {
    width: 100%;
    min-width: 0;
  }

  /* Data filters: two-column grid instead of a tall single stack */
  .filter-row-compact.data-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .data-row .filter-group-compact {
    min-width: 0;
    width: 100%;
  }

  .data-row .compact-select {
    width: 100%;
    min-width: 0;
  }
}

</style>
